import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from 'react';
import {
  Form,
  Select,
  Typography,
  Space,
  Checkbox,
  Input,
  FormInstance,
  Tooltip,
} from 'antd';
import { DeleteOutlined, InfoCircleFilled } from '@ant-design/icons';

import { uniqueId } from 'lodash-es';
import {
  BillingCategory,
  CostDistributionConfig,
  JobCostDistributionBannerDTO,
  PurchaseOrder,
  CostDistributionPayload,
} from 'domains/orderDomain';
import { UserCostDistributionConfig } from 'domains/accountDomain';
import { User } from 'domains/appDomain';
import { ShippingAccountTypeEnum } from 'domains/jobconfigDomain';
import { SiteRoleEnum } from 'domains/roleDomain';
import { RevxButton } from 'components/RevxButton';
import { SectionHeading, TextHeavy } from 'components/RevxTypography';
import { RevxDateWeek } from 'components/RevxDateWeek';
import { RevxDate } from 'components/RevxDate';
import { RevxMoney } from 'components/RevxMoney';
import orderService from 'services/orderService';
import accountService from 'services/accountService';
import { Organization } from 'domains/orgDomain';
import { CHECKOUT_ALBERTSONS_ORGS } from 'utils/constants';
import { toast } from 'components/RevxMessage';
import { message } from 'antd';
import { Contact } from 'domains/orgDomain';
import './CostDistributionSection.less';

export interface BannerSplitItem {
  id: string;
  bannerId: number | undefined;
  quantity: number | undefined;
  percent: number | undefined;
  amount: number | undefined;
}

export interface SplitItem {
  id: string;
  configId: number | undefined;
  quantity: number | undefined;
  percent: number | undefined;
  amount: number | undefined;
  banners: Array<JobCostDistributionBannerDTO>;
  isBannerSplitEnabled: boolean;
  bannerSplitItems: Array<BannerSplitItem>;
}

export interface CategorySplitItem {
  id: string;
  categoryId: string | undefined;
  categoryName: string | undefined;
  quantity: number | undefined;
  percent: number | undefined;
  amount: number | undefined;
  configs: Array<CostDistributionConfig>;
  isSubSplitEnabled: boolean;
  subSplitItems: Array<SplitItem>;
}

interface CostDistributionSectionProps {
  user?: User | null;
  org: Organization | undefined;
  orderItems: Partial<PurchaseOrder>;
  formTotal: { orderTotal: number | undefined };
  billingDetailForm: FormInstance;
  billingContact?: Contact;
  /**
   * Existing cost distribution to edit. When provided the section hydrates its
   * state from it (instead of the user profile default) so the previously saved
   * values are shown for editing.
   */
  initialPayload?: CostDistributionPayload;
  /** Render inside a narrow container (e.g. a modal) instead of the checkout column. */
  compact?: boolean;
  /**
   * Hide the "update my profile" / "update Billing Contact" checkboxes. They
   * are checkout defaults for future orders and do not apply when editing the
   * distribution of an existing job.
   */
  hideProfileOptions?: boolean;
}

export interface CostDistributionSectionRef {
  getPayload: () => CostDistributionPayload | undefined;
}

export const CostDistributionSection = forwardRef<
  CostDistributionSectionRef,
  CostDistributionSectionProps
>(
  (
    {
      user,
      org,
      orderItems,
      formTotal,
      billingDetailForm,
      billingContact,
      initialPayload,
      compact = false,
      hideProfileOptions = false,
    },
    ref
  ) => {
  const createInitialCategorySplitItem = useCallback(
    () => ({
      id: uniqueId('cat-split-'),
      categoryId: undefined,
      categoryName: undefined,
      quantity: undefined,
      percent: undefined,
      amount: undefined,
      configs: [],
      isSubSplitEnabled: false,
      subSplitItems: [
        {
          id: uniqueId('split-'),
          configId: undefined,
          quantity: undefined,
          percent: undefined,
          amount: undefined,
          banners: [],
          isBannerSplitEnabled: false,
          bannerSplitItems: [
            {
              id: uniqueId('banner-split-'),
              bannerId: undefined,
              quantity: undefined,
              percent: undefined,
              amount: undefined,
            },
          ],
        },
      ],
    }),
    []
  );

  const [categories, setCategories] = useState<Array<BillingCategory>>([]);
  const [comments, setComments] = useState('');

  const [isApplyToAllJobs, setIsApplyToAllJobs] = useState(true);
  const [saveUserProfile, setSaveUserProfile] = useState(false);
  const [updateContactOracleBillingCode, setUpdateContactOracleBillingCode] =
    useState(false);
  const [userConfig, setUserConfig] =
    useState<UserCostDistributionConfig | undefined>();
  const [distributions, setDistributions] = useState<
    Record<string, Array<CategorySplitItem>>
  >({
    '-1': [createInitialCategorySplitItem()],
  });

  // Fetch full account details to get cost distribution config
  useEffect(() => {
    const fetchUserConfig = async () => {
      if (!user?.userId || !org?.id) return;

      try {
        const res: any = await accountService.getUserById({ id: user.userId });
        const account = res?.data || res;

        if (account?.costDistributionConfigMap?.[org.id]) {
          setUserConfig(account.costDistributionConfigMap[org.id]);
        }
      } catch (error) {
        console.error(
          'Failed to fetch user account details for cost distribution config:',
          error
        );
      }
    };

    fetchUserConfig();
  }, [user?.userId, org?.id]);

  // Apply user cost distribution config default
  useEffect(() => {
    const applyUserConfig = async () => {
      // Editing an existing distribution: the saved values win over the profile default
      if (initialPayload) return;
      // Condition: User has config for this Org AND (single job OR applyToAllJobs)
      if (
        !userConfig ||
        !(isApplyToAllJobs || orderItems?.jobs?.length === 1)
      ) {
        return;
      }

      const jobIdsToCheck = isApplyToAllJobs
        ? ['-1']
        : orderItems?.jobs?.map((j) => String(j.jobId)) || [];

      // Check if current state is pristine (only initial empty item) for all target jobs
      const isPristine = jobIdsToCheck.every((jid) => {
        const items = distributions[jid];
        return (
          items &&
          items.length === 1 &&
          !items[0].categoryId &&
          items[0].subSplitItems.length === 1 &&
          !items[0].subSplitItems[0].configId
        );
      });

      if (!isPristine) return;

      // Handle Manual Oracle String Preferred
      if (userConfig.isManualOracleString) {
        setIsManualOracleString(true);
        if (userConfig.manualOracleString) {
          setManualOracleString(userConfig.manualOracleString);
        }
        return;
      }

      if (userConfig.categories && userConfig.categories.length > 0) {
        try {
          // Ensure we have categories loaded to get correct names
          let currentCategories = categories;
          if (currentCategories.length === 0) {
            currentCategories = await orderService.listCategories();
            setCategories(currentCategories);
          }

          const newCategoryItems: CategorySplitItem[] = [];

          for (const catConf of userConfig.categories) {
            if (!catConf.categoryId) continue;

            // Try to find category from loaded categories to ensure exact ID match (handling string/number types)
            // and to get the correct category name if missing
            const categoryIdStr = String(catConf.categoryId);
            const matchingCategory = currentCategories.find(
              (c) => String(c.id) === categoryIdStr
            );

            // Use the ID from the loaded categories if found to ensure strict equality (===) matches in the Select component
            const finalCategoryId = matchingCategory
              ? matchingCategory.id
              : // @ts-ignore - handling potential number/string mismatch if not found in list
                catConf.categoryId;

            const categoryName =
              matchingCategory?.category || catConf.categoryName;

            // Fetch configs for this category
            const availableConfigs =
              await orderService.listCostDistributionConfigs(categoryIdStr);

            const newItem: CategorySplitItem = {
              id: uniqueId('cat-split-'),
              categoryId: finalCategoryId as string, // Cast to string to satisfy type, though at runtime it might be the type from matchingCategory.id
              categoryName: categoryName,
              quantity: undefined,
              percent: catConf.percent,
              amount: undefined,
              configs: availableConfigs,
              isSubSplitEnabled: true,
              subSplitItems: [],
            };

            if (catConf.subSplits && catConf.subSplits.length > 0) {
              for (const subConf of catConf.subSplits) {
                const newSubItem: SplitItem = {
                  id: uniqueId('split-'),
                  configId: subConf.configId,
                  quantity: undefined,
                  percent: subConf.percent,
                  amount: undefined,
                  banners: [],
                  isBannerSplitEnabled: false,
                  bannerSplitItems: [],
                };

                // Handle Banners if Store Allocation or Division Admin
                // Use categoryName resolved above
                if (hasBannerSupport(categoryName)) {
                  const selectedConfig = availableConfigs.find(
                    (c) => c.id === subConf.configId
                  );
                  if (selectedConfig) {
                    const bannerList = await orderService.queryBanners(
                      selectedConfig.site,
                      selectedConfig.siteDesc
                    );
                    newSubItem.banners = bannerList;

                    if (subConf.banners && subConf.banners.length > 0) {
                      for (const bannerConf of subConf.banners) {
                        newSubItem.bannerSplitItems.push({
                          id: uniqueId('banner-split-'),
                          bannerId: bannerConf.bannerId,
                          quantity: undefined,
                          percent: bannerConf.percent,
                          amount: undefined,
                        });
                      }
                    } else {
                      newSubItem.bannerSplitItems.push({
                        id: uniqueId('banner-split-'),
                        bannerId: undefined,
                        quantity: undefined,
                        percent: undefined,
                        amount: undefined,
                      });
                    }
                  } else {
                    newSubItem.bannerSplitItems.push({
                      id: uniqueId('banner-split-'),
                      bannerId: undefined,
                      quantity: undefined,
                      percent: undefined,
                      amount: undefined,
                    });
                  }
                } else {
                  newSubItem.bannerSplitItems.push({
                    id: uniqueId('banner-split-'),
                    bannerId: undefined,
                    quantity: undefined,
                    percent: undefined,
                    amount: undefined,
                  });
                }
                newItem.subSplitItems.push(newSubItem);
              }
            } else {
              newItem.subSplitItems.push({
                id: uniqueId('split-'),
                configId: undefined,
                quantity: undefined,
                percent: undefined,
                amount: undefined,
                banners: [],
                isBannerSplitEnabled: false,
                bannerSplitItems: [
                  {
                    id: uniqueId('banner-split-'),
                    bannerId: undefined,
                    quantity: undefined,
                    percent: undefined,
                    amount: undefined,
                  },
                ],
              });
            }
            newCategoryItems.push(newItem);
          }

          if (newCategoryItems.length > 0) {
            setDistributions((prev) => {
              const next = { ...prev };
              jobIdsToCheck.forEach((jid) => {
                let jobTotalQty = 0;
                let jobTotalAmount = 0;

                if (jid === '-1') {
                  jobTotalQty =
                    orderItems?.jobs?.reduce(
                      (sum: number, job: any) => sum + (job.quantity || 0),
                      0
                    ) || 0;
                  jobTotalAmount = formTotal.orderTotal || 0;
                } else {
                  const job = orderItems?.jobs?.find(
                    (j) => String(j.jobId) === jid
                  );
                  jobTotalQty = job?.quantity || 0;
                  jobTotalAmount = job?.totalCharge || 0;
                }

                const jobItems = JSON.parse(JSON.stringify(newCategoryItems));

                jobItems.forEach((cat: any) => {
                  if (cat.percent !== undefined && cat.percent !== null) {
                    cat.quantity = Math.round(
                      jobTotalQty * (Number(cat.percent) / 100)
                    );
                    cat.amount = Number(
                      (jobTotalAmount * (Number(cat.percent) / 100)).toFixed(2)
                    );
                  }

                  if (cat.subSplitItems) {
                    cat.subSplitItems.forEach((sub: any) => {
                      if (sub.percent !== undefined && sub.percent !== null) {
                        sub.quantity = Math.round(
                          jobTotalQty * (Number(sub.percent) / 100)
                        );
                        sub.amount = Number(
                          (
                            jobTotalAmount *
                            (Number(sub.percent) / 100)
                          ).toFixed(2)
                        );
                      }

                      if (sub.bannerSplitItems) {
                        sub.bannerSplitItems.forEach((banner: any) => {
                          if (
                            banner.percent !== undefined &&
                            banner.percent !== null
                          ) {
                            banner.quantity = Math.round(
                              jobTotalQty * (Number(banner.percent) / 100)
                            );
                            banner.amount = Number(
                              (
                                jobTotalAmount *
                                (Number(banner.percent) / 100)
                              ).toFixed(2)
                            );
                          }
                        });
                      }
                    });
                  }
                });

                next[jid] = jobItems;
              });
              return next;
            });
          }
        } catch (error) {
          console.error(
            'Failed to apply user cost distribution config:',
            error
          );
        }
      }
    };

    applyUserConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userConfig, isApplyToAllJobs, orderItems?.jobs]);

  // Initialize distributions for each job when orderItems change
  useEffect(() => {
    if (orderItems?.jobs && orderItems.jobs.length > 1) {
      setDistributions((prev) => {
        const newDistributions = { ...prev };
        orderItems.jobs?.forEach((job) => {
          if (!newDistributions[job.jobId]) {
            newDistributions[job.jobId] = [createInitialCategorySplitItem()];
          }
        });
        return newDistributions;
      });
    }
  }, [orderItems?.jobs, createInitialCategorySplitItem]);

  const getItemsByJobId = useCallback(
    (jobId: string | number) => {
      return distributions[String(jobId)] || [];
    },
    [distributions]
  );

  const setUpdateDistributions = useCallback(
    (
      jobId: string | number,
      updater: (items: CategorySplitItem[]) => CategorySplitItem[]
    ) => {
      setDistributions((prev) => ({
        ...prev,
        [String(jobId)]: updater(prev[String(jobId)] || []),
      }));
    },
    []
  );

  const [isManualOracleString, setIsManualOracleString] =
    useState<boolean>(false);
  const [manualOracleString, setManualOracleString] = useState<string>('');
  const [savedDistributions, setSavedDistributions] = useState<
    Record<string, Array<CategorySplitItem>>
  >({});
  const [lastChangedId, setLastChangedId] = useState<string | undefined>();
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string[]>
  >({});
  const [oracleStrings, setOracleStrings] = useState<Record<string, string>>(
    {}
  );

  useEffect(() => {
    // Editing an existing distribution: keep the saved oracle string
    if (initialPayload) return;
    const oracleBillingCode = billingContact?.oracleBillingCode?.trim();
    if (!oracleBillingCode) {
      setIsManualOracleString(false);
      setManualOracleString('');
      return;
    }

    setIsManualOracleString(true);
    setManualOracleString(oracleBillingCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingContact?.id, billingContact?.oracleBillingCode]);

  const hydratedFromPayloadRef = useRef(false);

  // Hydrate the whole section from an existing cost distribution so the user
  // edits the previously saved values instead of an empty form.
  useEffect(() => {
    const hydrateFromPayload = async () => {
      if (!initialPayload || hydratedFromPayloadRef.current) return;
      hydratedFromPayloadRef.current = true;

      setComments(initialPayload.comments || '');
      if (!hideProfileOptions) {
        setSaveUserProfile(Boolean(initialPayload.saveUserProfile));
        setUpdateContactOracleBillingCode(
          Boolean(initialPayload.updateContactOracleBillingCode)
        );
      }

      const applyToAllJobs = initialPayload.isApplyToAllJobs !== false;
      setIsApplyToAllJobs(applyToAllJobs);

      if (initialPayload.isManualOracleString) {
        setIsManualOracleString(true);
        setManualOracleString(initialPayload.manualOracleString || '');
        return;
      }
      setIsManualOracleString(false);

      const payloadCategories = initialPayload.categories || [];
      if (payloadCategories.length === 0) return;

      try {
        let currentCategories = categories;
        if (currentCategories.length === 0) {
          currentCategories = await orderService.listCategories();
          setCategories(currentCategories);
        }

        const configsCache: Record<string, Array<CostDistributionConfig>> = {};
        const hydrated: Record<string, Array<CategorySplitItem>> = {};

        // Saved ids go stale whenever the cost distribution data is re-imported,
        // so the saved names decide what gets selected and the ids only serve as
        // a fallback when a name no longer exists.
        const configDisplayName = (
          config: CostDistributionConfig,
          catName: string | undefined
        ) =>
          formatConfigDesc(
            catName === 'Corporate' ? config.costCenterDesc : config.siteDesc,
            catName,
            catName === 'Corporate' ? config.costCenter : config.site
          );

        for (const catPayload of payloadCategories) {
          if (!catPayload.categoryId && !catPayload.categoryName) continue;

          const jobKey =
            applyToAllJobs || catPayload.jobId === undefined
              ? '-1'
              : String(catPayload.jobId);

          const matchingCategory =
            currentCategories.find(
              (c) => c.category === catPayload.categoryName
            ) ||
            currentCategories.find(
              (c) => String(c.id) === String(catPayload.categoryId)
            );
          const categoryName =
            matchingCategory?.category || catPayload.categoryName;
          const categoryId = matchingCategory?.id ?? catPayload.categoryId;
          if (!categoryId) continue;

          const categoryIdStr = String(categoryId);
          if (!configsCache[categoryIdStr]) {
            configsCache[categoryIdStr] =
              await orderService.listCostDistributionConfigs(categoryIdStr);
          }
          const availableConfigs = configsCache[categoryIdStr];

          const subSplits = catPayload.subSplits || [];
          const newItem: CategorySplitItem = {
            id: uniqueId('cat-split-'),
            categoryId: categoryId as string,
            categoryName,
            quantity: catPayload.quantity,
            percent: catPayload.percent,
            amount: catPayload.amount,
            configs: availableConfigs,
            isSubSplitEnabled: subSplits.length > 1,
            subSplitItems: [],
          };

          for (const subPayload of subSplits) {
            const bannerPayloads = subPayload.banners || [];
            const matchingConfig =
              (subPayload.siteName
                ? availableConfigs.find(
                    (c) =>
                      configDisplayName(c, categoryName) === subPayload.siteName
                  )
                : undefined) ||
              availableConfigs.find((c) => c.id === subPayload.configId);

            const newSubItem: SplitItem = {
              id: uniqueId('split-'),
              configId: matchingConfig?.id ?? subPayload.configId,
              quantity: subPayload.quantity,
              percent: subPayload.percent,
              amount: subPayload.amount,
              banners: [],
              isBannerSplitEnabled: bannerPayloads.length > 1,
              bannerSplitItems: [],
            };

            if (hasBannerSupport(categoryName) && matchingConfig) {
              newSubItem.banners = await orderService.queryBanners(
                matchingConfig.site,
                matchingConfig.siteDesc
              );
            }

            if (bannerPayloads.length > 0) {
              bannerPayloads.forEach((bannerPayload) => {
                const matchingBanner =
                  (bannerPayload.bannerName
                    ? newSubItem.banners.find(
                        (b) => b.banner === bannerPayload.bannerName
                      )
                    : undefined) ||
                  newSubItem.banners.find(
                    (b) => b.id === bannerPayload.bannerId
                  );
                newSubItem.bannerSplitItems.push({
                  id: uniqueId('banner-split-'),
                  bannerId: matchingBanner?.id ?? bannerPayload.bannerId,
                  quantity: bannerPayload.quantity,
                  percent: bannerPayload.percent,
                  amount: bannerPayload.amount,
                });
              });
            } else {
              newSubItem.bannerSplitItems.push({
                id: uniqueId('banner-split-'),
                bannerId: undefined,
                quantity: undefined,
                percent: undefined,
                amount: undefined,
              });
            }

            newItem.subSplitItems.push(newSubItem);
          }

          if (newItem.subSplitItems.length === 0) {
            newItem.subSplitItems.push({
              id: uniqueId('split-'),
              configId: undefined,
              quantity: undefined,
              percent: undefined,
              amount: undefined,
              banners: [],
              isBannerSplitEnabled: false,
              bannerSplitItems: [
                {
                  id: uniqueId('banner-split-'),
                  bannerId: undefined,
                  quantity: undefined,
                  percent: undefined,
                  amount: undefined,
                },
              ],
            });
          }

          hydrated[jobKey] = [...(hydrated[jobKey] || []), newItem];
        }

        // The saved amounts belong to the order the distribution was created on.
        // An adjustment changes the price, so every level is recalculated from
        // its percentage against the total this section is splitting right now.
        const applyPercentToRow = (
          row: {
            quantity: number | undefined;
            percent: number | undefined;
            amount: number | undefined;
          },
          totalQuantity: number,
          totalAmount: number
        ) => {
          if (row.percent === undefined || row.percent === null) return;
          const ratio = Number(row.percent) / 100;
          if (totalQuantity > 0) {
            row.quantity = Math.round(totalQuantity * ratio);
          }
          if (totalAmount > 0) {
            row.amount = Number((totalAmount * ratio).toFixed(2));
          }
        };

        Object.entries(hydrated).forEach(([jobKey, items]) => {
          const job = orderItems?.jobs?.find(
            (j) => String(j.jobId) === jobKey
          );
          const jobTotalQuantity =
            jobKey === '-1'
              ? orderItems?.jobs?.reduce(
                  (sum: number, j: any) => sum + (j.quantity || 0),
                  0
                ) || 0
              : job?.quantity || 0;
          const jobTotalAmount =
            jobKey === '-1'
              ? formTotal.orderTotal || 0
              : job?.totalCharge || 0;

          items.forEach((category) => {
            applyPercentToRow(category, jobTotalQuantity, jobTotalAmount);
            category.subSplitItems.forEach((sub) => {
              applyPercentToRow(sub, jobTotalQuantity, jobTotalAmount);
              sub.bannerSplitItems.forEach((banner) =>
                applyPercentToRow(banner, jobTotalQuantity, jobTotalAmount)
              );
            });
          });
        });

        if (Object.keys(hydrated).length > 0) {
          setDistributions((prev) => ({ ...prev, ...hydrated }));
        }
      } catch (error) {
        console.error('Failed to apply existing cost distribution:', error);
      }
    };

    hydrateFromPayload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPayload]);

  const isFieldError = useCallback(
    (id: string, field: string) => {
      return validationErrors[id]?.includes(field);
    },
    [validationErrors]
  );

  const clearFieldError = useCallback((id: string, field: string) => {
    setValidationErrors((prev) => {
      if (!prev[id]) return prev;
      const newFields = prev[id].filter((f) => f !== field);
      if (newFields.length === 0) {
        const { [id]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [id]: newFields };
    });
  }, []);

  const errorInputStyle = useMemo(
    () => ({
      border: '1px solid #ff4d4f',
      boxShadow: '0 0 0 2px rgba(255, 77, 79, 0.2)',
    }),
    []
  );

  const errorSelectStyle = useMemo(
    () => ({
      border: '1px solid #ff4d4f',
      borderRadius: '4px',
    }),
    []
  );

  const parseNumberInput = useCallback((val: string) => {
    if (val === '' || val === undefined) return undefined;

    let cleaned = val.replace(/[^-0-9.]/g, '').replace(/-/g, '');

    const parts = cleaned.split('.');
    if (parts.length > 2) {
      cleaned = parts[0] + '.' + parts.slice(1).join('');
    }

    if (cleaned === '') return undefined;

    if (cleaned.length > 1 && cleaned.startsWith('0') && cleaned[1] !== '.') {
      cleaned = cleaned.replace(/^0+/, '') || '0';
    }

    return cleaned;
  }, []);

  const formatConfigDesc = useCallback(
    (
      desc: string | undefined,
      categoryName: string | undefined,
      prefixValue: string | undefined
    ) => {
      if (!desc) return '';
      const isCorporate = categoryName === 'Corporate';
      const defaultPrefix = isCorporate ? 'Cost Center' : 'Site';
      const prefix = prefixValue || defaultPrefix;

      if (!desc.toLowerCase().startsWith(prefix.toLowerCase())) {
        return `${prefix} - ${desc}`;
      }
      return desc;
    },
    []
  );

  const hasBannerSupport = useCallback(
    (categoryName: string | undefined) =>
      categoryName === 'Store Allocation' || categoryName === 'Division Admin',
    []
  );

  const orderTotalQuantity = useMemo(() => {
    return (
      orderItems?.jobs?.reduce(
        (sum: number, job: any) => sum + (job.quantity || 0),
        0
      ) || 0
    );
  }, [orderItems]);

  const getJobCalculations = useCallback(
    (jobId: string | number) => {
      const items = getItemsByJobId(jobId);
      const job = orderItems?.jobs?.find(
        (j) => String(j.jobId) === String(jobId)
      );
      const totalQty = jobId === '-1' ? orderTotalQuantity : job?.quantity || 0;
      const totalAmount =
        jobId === '-1' ? formTotal.orderTotal || 0 : job?.totalCharge || 0;

      const totals = items.reduce(
        (acc, item) => {
          let itemQty = 0;
          let itemPercent = 0;
          let itemAmount = 0;

          if (items.length > 1) {
            itemQty = Number(item.quantity) || 0;
            itemPercent = Number(item.percent) || 0;
            itemAmount = Number(item.amount) || 0;
          } else if (item.subSplitItems.length > 1) {
            item.subSplitItems.forEach((sub) => {
              itemQty += Number(sub.quantity) || 0;
              itemPercent += Number(sub.percent) || 0;
              itemAmount += Number(sub.amount) || 0;
            });
          } else if (item.subSplitItems.length === 1) {
            const subItem = item.subSplitItems[0];
            if (subItem.bannerSplitItems.length > 1) {
              subItem.bannerSplitItems.forEach((banner) => {
                itemQty += Number(banner.quantity) || 0;
                itemPercent += Number(banner.percent) || 0;
                itemAmount += Number(banner.amount) || 0;
              });
            } else {
              itemQty = Number(subItem.quantity) || 0;
              itemPercent = Number(subItem.percent) || 0;
              itemAmount = Number(subItem.amount) || 0;
            }
          } else {
            itemQty = Number(item.quantity) || 0;
            itemPercent = Number(item.percent) || 0;
            itemAmount = Number(item.amount) || 0;
          }

          return {
            quantity: acc.quantity + itemQty,
            percent: acc.percent + itemPercent,
            amount: acc.amount + itemAmount,
          };
        },
        { quantity: 0, percent: 0, amount: 0 }
      );

      const isQuantityFullyAllocated = totals.quantity === totalQty;
      const isPercentFullyAllocated = Math.abs(totals.percent - 100) < 0.01;

      let finalTotals = totals;
      if (
        isQuantityFullyAllocated &&
        isPercentFullyAllocated &&
        totalAmount > 0
      ) {
        finalTotals = { ...totals, amount: totalAmount };
      }

      const isSplitActive =
        items.length > 1 ||
        items.some((cat) => cat.subSplitItems.length > 1) ||
        items.some((cat) =>
          cat.subSplitItems.some((sub) => sub.bannerSplitItems.length > 1)
        );

      const remainingQuantity = totalQty - finalTotals.quantity;
      const remainingPercent = 100 - finalTotals.percent;
      const hasOverAllocation = remainingQuantity < 0;

      return {
        totals: finalTotals,
        isSplitActive,
        remainingQuantity,
        remainingPercent,
        hasOverAllocation,
        totalQty,
        totalAmount,
      };
    },
    [
      getItemsByJobId,
      orderItems?.jobs,
      orderTotalQuantity,
      formTotal.orderTotal,
    ]
  );

  useImperativeHandle(ref, () => ({
    getPayload: () => {
      // Validate manual oracle string if checkbox is checked
      if (isManualOracleString && !manualOracleString.trim()) {
        toast.error('Please enter the Oracle billing line.');
        return undefined;
      }

      // Manual oracle mode: when user already provided a billing line,
      // skip cost distribution category/site validation entirely.
      if (isManualOracleString && manualOracleString.trim()) {
        return {
          categories: [],
          isManualOracleString,
          manualOracleString,
          jobs: orderItems?.jobs?.map((job) => ({
            jobId: job.jobId,
            jobName: job.jobName,
            quantity: job.quantity,
            totalCharge: job.totalCharge,
          })),
          comments,
          isApplyToAllJobs,
          saveUserProfile: hideProfileOptions ? false : saveUserProfile,
          updateContactOracleBillingCode: hideProfileOptions
            ? false
            : updateContactOracleBillingCode,
        };
      }

      const activeJobIds = isApplyToAllJobs
        ? ['-1']
        : orderItems?.jobs?.map((j) => String(j.jobId)) || [];

      const allInitial = activeJobIds.every((jid) => {
        const items = getItemsByJobId(jid);
        return (
          items.length === 1 &&
          !items[0].categoryId &&
          items[0].subSplitItems.length === 1 &&
          !items[0].subSplitItems[0].configId
        );
      });

      if (allInitial) {
        // Category and Site are required - cannot submit without selecting them
        const initErrors: Record<string, string[]> = {};
        for (const jid of activeJobIds) {
          const items = getItemsByJobId(jid);
          for (const cat of items) {
            initErrors[cat.id] = ['categoryId'];
            for (const sub of cat.subSplitItems) {
              initErrors[sub.id] = ['configId'];
            }
          }
        }
        setValidationErrors(initErrors);
        toast.error('Please select a Category and Site/Cost Center.');
        return undefined;
      }

      const errors: Record<string, string[]> = {};
      let hasError = false;
      const allCategoriesPayload: any[] = [];

      for (const jid of activeJobIds) {
        const items = getItemsByJobId(jid);
        const { totals, isSplitActive, totalQty } = getJobCalculations(jid);
        const job = orderItems?.jobs?.find((j) => String(j.jobId) === jid);

        if (isSplitActive && totals.quantity !== totalQty) {
          toast.error(
            isApplyToAllJobs
              ? 'The allocated quantity does not match the total order quantity.'
              : `The allocated quantity for job "${job?.jobName}" does not match its total quantity.`
          );
          return undefined;
        }

        for (const cat of items) {
          const catErrors: string[] = [];
          if (!cat.categoryId) catErrors.push('categoryId');
          if (items.length > 1) {
            if (!cat.quantity) catErrors.push('quantity');
            if (!cat.percent) catErrors.push('percent');
            if (!cat.amount) catErrors.push('amount');
          }
          if (catErrors.length > 0) {
            errors[cat.id] = catErrors;
            hasError = true;
          }

          for (const sub of cat.subSplitItems) {
            const subErrors: string[] = [];
            if (!sub.configId) subErrors.push('configId');
            if (cat.subSplitItems.length > 1) {
              if (!sub.quantity) subErrors.push('quantity');
              if (!sub.percent) subErrors.push('percent');
              if (!sub.amount) subErrors.push('amount');
            }
            if (subErrors.length > 0) {
              errors[sub.id] = subErrors;
              hasError = true;
            }

            if (hasBannerSupport(cat.categoryName) && sub.banners?.length > 0) {
              for (const banner of sub.bannerSplitItems) {
                const bErrors: string[] = [];
                // Banner is optional, so we do not push 'bannerId' error
                if (sub.bannerSplitItems.length > 1) {
                  if (!banner.quantity) bErrors.push('quantity');
                  if (!banner.percent) bErrors.push('percent');
                  if (!banner.amount) bErrors.push('amount');
                }
                if (bErrors.length > 0) {
                  errors[banner.id] = bErrors;
                  hasError = true;
                }
              }
            }
          }
        }

        for (const cat of items) {
          const { totalQty, totalAmount } = getJobCalculations(jid);
          const catQty = cat.quantity ?? totalQty;
          const catPercent = cat.percent ?? 100;
          const catAmt = cat.amount ?? totalAmount;

          allCategoriesPayload.push({
            categoryId: cat.categoryId,
            categoryName: cat.categoryName,
            jobId: jid === '-1' ? undefined : Number(jid),
            subSplits: cat.subSplitItems
              .filter((sub) => sub.configId)
              .map((sub) => {
                const config = cat.configs.find((c) => c.id === sub.configId);
                const subQty = sub.quantity ?? catQty;
                // A sub-split with nothing filled in covers the whole category, so it inherits the
                // category's share. Percentages are against the job total, not against the
                // category, so 100 here would claim the whole job.
                const subPercent = sub.percent ?? catPercent;
                const subAmt = sub.amount ?? catAmt;

                return {
                  configId: sub.configId,
                  siteName: formatConfigDesc(
                    cat.categoryName === 'Corporate'
                      ? config?.costCenterDesc
                      : config?.siteDesc,
                    cat.categoryName,
                    cat.categoryName === 'Corporate'
                      ? config?.costCenter
                      : config?.site
                  ),
                  quantity: subQty,
                  percent: subPercent,
                  amount: subAmt,
                  banners: sub.bannerSplitItems
                    .filter((b) => b.bannerId && b.bannerId !== -1)
                    .map((b) => ({
                      bannerId: b.bannerId,
                      bannerName: sub.banners.find((bi) => bi.id === b.bannerId)
                        ?.banner,
                      quantity: b.quantity ?? subQty,
                      percent: b.percent ?? subPercent,
                      amount: b.amount ?? subAmt,
                    })),
                };
              }),
            quantity: catQty,
            percent: catPercent,
            amount: catAmt,
          });
        }
      }

      if (hasError) {
        setValidationErrors(errors);
        toast.error('Please complete all required fields.');
        return undefined;
      }

      return {
        categories: allCategoriesPayload,
        isManualOracleString,
        manualOracleString: isManualOracleString ? manualOracleString : '',
        jobs: orderItems?.jobs?.map((job) => ({
          jobId: job.jobId,
          jobName: job.jobName,
          quantity: job.quantity,
          totalCharge: job.totalCharge,
        })),
        comments,
        isApplyToAllJobs,
        saveUserProfile: hideProfileOptions ? false : saveUserProfile,
        updateContactOracleBillingCode: hideProfileOptions
          ? false
          : updateContactOracleBillingCode,
      };
    },
  }));

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const categoryList = await orderService.listCategories();
        setCategories(categoryList);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };
    fetchCategories();
  }, []);

  // Copy oracle string to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(
      () => {
        message.success('Oracle String copied to clipboard');
      },
      (err) => {
        console.error('Failed to copy:', err);
        toast.error('Failed to copy Oracle String');
      }
    );
  };

  // Auto-generate oracle string when data is complete
  useEffect(() => {
    const generateOracleString = async () => {
      if (isManualOracleString) return;

      const activeJobIds = isApplyToAllJobs
        ? ['-1']
        : orderItems?.jobs?.map((j) => String(j.jobId)) || [];

      const allInitial = activeJobIds.every((jid) => {
        const items = getItemsByJobId(jid);
        return (
          items.length === 1 &&
          !items[0].categoryId &&
          items[0].subSplitItems.length === 1 &&
          !items[0].subSplitItems[0].configId
        );
      });

      if (allInitial) return;

      // Only check if Category, Site/Cost Center, and Banner are selected
      // No need to check quantity, percent, amount, or totals
      for (const jid of activeJobIds) {
        const items = getItemsByJobId(jid);

        for (const cat of items) {
          if (!cat.categoryId) return;

          for (const sub of cat.subSplitItems) {
            if (!sub.configId) return;

            // Banner is optional, so we do not return early if missing
          }
        }
      }

      // All validations passed, generate payload
      const allCategoriesPayload: any[] = [];

      for (const jid of activeJobIds) {
        const items = getItemsByJobId(jid);

        for (const cat of items) {
          const { totalQty, totalAmount } = getJobCalculations(jid);
          const catQty = cat.quantity ?? totalQty;
          const catPercent = cat.percent ?? 100;
          const catAmt = cat.amount ?? totalAmount;

          allCategoriesPayload.push({
            categoryId: cat.categoryId,
            categoryName: cat.categoryName,
            jobId: jid === '-1' ? undefined : Number(jid),
            subSplits: cat.subSplitItems
              .filter((sub) => sub.configId)
              .map((sub) => {
                const config = cat.configs.find((c) => c.id === sub.configId);
                const subQty = sub.quantity ?? catQty;
                // A sub-split with nothing filled in covers the whole category, so it inherits the
                // category's share. Percentages are against the job total, not against the
                // category, so 100 here would claim the whole job.
                const subPercent = sub.percent ?? catPercent;
                const subAmt = sub.amount ?? catAmt;

                return {
                  configId: sub.configId,
                  siteName: formatConfigDesc(
                    cat.categoryName === 'Corporate'
                      ? config?.costCenterDesc
                      : config?.siteDesc,
                    cat.categoryName,
                    cat.categoryName === 'Corporate'
                      ? config?.costCenter
                      : config?.site
                  ),
                  quantity: subQty,
                  percent: subPercent,
                  amount: subAmt,
                  banners: sub.bannerSplitItems
                    .filter((b) => b.bannerId && b.bannerId !== -1)
                    .map((b) => ({
                      bannerId: b.bannerId,
                      bannerName: sub.banners.find((bi) => bi.id === b.bannerId)
                        ?.banner,
                      quantity: b.quantity ?? subQty,
                      percent: b.percent ?? subPercent,
                      amount: b.amount ?? subAmt,
                    })),
                };
              }),
            quantity: catQty,
            percent: catPercent,
            amount: catAmt,
          });
        }
      }

      const payload = {
        categories: allCategoriesPayload,
        isManualOracleString: false,
        manualOracleString: '',
        jobs: orderItems?.jobs?.map((job) => ({
          jobId: job.jobId,
          jobName: job.jobName,
          quantity: job.quantity,
          totalCharge: job.totalCharge,
        })),
        isApplyToAllJobs,
      };

      try {
        const response: any = await orderService.generateOracleString(payload);
        console.log('Oracle string API response:', response);

        // Oracle strings are in oracleSettings array
        const oracleSettings =
          response?.oracleSettings || response?.data?.oracleSettings || [];
        console.log('Oracle settings:', oracleSettings);

        if (
          oracleSettings &&
          Array.isArray(oracleSettings) &&
          oracleSettings.length > 0
        ) {
          const newOracleStrings: Record<string, string> = {};

          for (const jid of activeJobIds) {
            const items = getItemsByJobId(jid);
            for (const cat of items) {
              for (const sub of cat.subSplitItems) {
                const config = cat.configs.find((c) => c.id === sub.configId);
                if (!config) continue;

                const hasBanners =
                  sub.bannerSplitItems.length > 0 &&
                  sub.banners?.length > 0 &&
                  sub.bannerSplitItems.some(
                    (b) => b.bannerId && b.bannerId !== -1
                  );

                if (hasBanners) {
                  // Has banners - match by category, site/costCenter, and banner
                  for (const banner of sub.bannerSplitItems) {
                    if (banner.bannerId && banner.bannerId !== -1) {
                      const bannerInfo = sub.banners.find(
                        (b) => b.id === banner.bannerId
                      );
                      const oracleSetting = oracleSettings.find((os: any) => {
                        const categoryMatch = os.category === cat.categoryName;
                        const siteMatch =
                          cat.categoryName === 'Corporate'
                            ? os.costCenter === config.costCenter
                            : os.site === config.site;
                        const bannerMatch = os.banner === bannerInfo?.banner;
                        return categoryMatch && siteMatch && bannerMatch;
                      });

                      if (oracleSetting?.oracleString) {
                        const key = `${jid}-${cat.id}-${sub.id}-${banner.id}`;
                        newOracleStrings[key] = oracleSetting.oracleString;
                        console.log(
                          `Banner oracle key: ${key}, value: ${oracleSetting.oracleString}`
                        );
                      }
                    }
                  }
                } else {
                  // No banners - match by category and site/costCenter
                  const oracleSetting = oracleSettings.find((os: any) => {
                    const categoryMatch = os.category === cat.categoryName;
                    const siteMatch =
                      cat.categoryName === 'Corporate'
                        ? os.costCenter === config.costCenter
                        : os.site === config.site;
                    // 不再要求 banner 必须为空，优先匹配没有 banner 的，如果找不到则匹配有 banner 的
                    return categoryMatch && siteMatch;
                  });

                  if (oracleSetting?.oracleString) {
                    const key = `${jid}-${cat.id}-${sub.id}`;
                    newOracleStrings[key] = oracleSetting.oracleString;
                    console.log(
                      `Site oracle key: ${key}, value: ${oracleSetting.oracleString}`
                    );
                  }
                }
              }
            }
          }

          console.log('Final oracle strings state:', newOracleStrings);
          setOracleStrings(newOracleStrings);
        } else {
          console.log('No oracle settings found in response');
        }
      } catch (error) {
        console.error('Failed to generate oracle string:', error);
      }
    };

    generateOracleString();
  }, [
    distributions,
    isManualOracleString,
    isApplyToAllJobs,
    orderItems?.jobs,
    getItemsByJobId,
    getJobCalculations,
    formatConfigDesc,
  ]);

  const handleAddCategorySplitItem = (jobId: string | number) => {
    setUpdateDistributions(jobId, (prevItems) => [
      ...prevItems,
      {
        id: uniqueId('cat-split-'),
        categoryId: undefined,
        categoryName: undefined,
        quantity: undefined,
        percent: undefined,
        amount: undefined,
        configs: [],
        isSubSplitEnabled: true,
        subSplitItems: [
          {
            id: uniqueId('split-'),
            configId: undefined,
            quantity: undefined,
            percent: undefined,
            amount: undefined,
            banners: [],
            isBannerSplitEnabled: false,
            bannerSplitItems: [
              {
                id: uniqueId('banner-split-'),
                bannerId: undefined,
                quantity: undefined,
                percent: undefined,
                amount: undefined,
              },
            ],
          },
        ],
      },
    ]);
  };

  const handleAddCategorySubSplitItem = (
    jobId: string | number,
    catItemId: string
  ) => {
    setUpdateDistributions(jobId, (prevItems) =>
      prevItems.map((item) => {
        if (item.id === catItemId) {
          return {
            ...item,
            subSplitItems: [
              ...item.subSplitItems,
              {
                id: uniqueId('split-'),
                configId: undefined,
                quantity: undefined,
                percent: undefined,
                amount: undefined,
                banners: [],
                isBannerSplitEnabled: false,
                bannerSplitItems: [
                  {
                    id: uniqueId('banner-split-'),
                    bannerId: undefined,
                    quantity: undefined,
                    percent: undefined,
                    amount: undefined,
                  },
                ],
              },
            ],
          };
        }
        return item;
      })
    );
  };

  const handleAddCategorySubSplitBannerItem = (
    jobId: string | number,
    categoryId: string,
    subSplitId: string
  ) => {
    setUpdateDistributions(jobId, (prevItems) =>
      prevItems.map((item) => {
        if (item.id === categoryId) {
          return {
            ...item,
            subSplitItems: item.subSplitItems.map((subItem) => {
              if (subItem.id === subSplitId) {
                return {
                  ...subItem,
                  bannerSplitItems: [
                    ...subItem.bannerSplitItems,
                    {
                      id: uniqueId('banner-split-'),
                      bannerId: undefined,
                      quantity: undefined,
                      percent: undefined,
                      amount: undefined,
                    },
                  ],
                };
              }
              return subItem;
            }),
          };
        }
        return item;
      })
    );
  };

  const handleRemoveCategorySplitItem = (
    jobId: string | number,
    id: string
  ) => {
    const items = getItemsByJobId(jobId);
    if (items.length > 1) {
      setUpdateDistributions(jobId, (prevItems) => {
        const filtered = prevItems.filter((item) => item.id !== id);
        // If only one category remains, clear its quantity/percent/amount
        if (filtered.length === 1) {
          filtered[0].quantity = undefined;
          filtered[0].percent = undefined;
          filtered[0].amount = undefined;
        }
        return filtered;
      });
    }
  };

  const updateSubSplitTotalsFromBanners = (
    jobId: string | number,
    subItem: SplitItem
  ) => {
    if (subItem.bannerSplitItems.length > 1) {
      const totals = subItem.bannerSplitItems.reduce(
        (acc, banner) => ({
          quantity: acc.quantity + (Number(banner.quantity) || 0),
          percent: acc.percent + (Number(banner.percent) || 0),
          amount: acc.amount + (Number(banner.amount) || 0),
        }),
        { quantity: 0, percent: 0, amount: 0 }
      );

      const job = orderItems?.jobs?.find(
        (j) => String(j.jobId) === String(jobId)
      );
      const totalAmount =
        jobId === '-1' ? formTotal.orderTotal || 0 : job?.totalCharge || 0;
      const isPercentFullyAllocated = Math.abs(totals.percent - 100) < 0.01;

      let finalAmount = totals.amount;
      if (isPercentFullyAllocated && totalAmount > 0 && totals.quantity > 0) {
        const totalQty =
          jobId === '-1' ? orderTotalQuantity : job?.quantity || 0;
        if (totalQty > 0) {
          finalAmount = (totals.quantity / totalQty) * totalAmount;
        }
      }

      subItem.quantity = totals.quantity || undefined;
      subItem.percent = totals.percent
        ? Number(totals.percent.toFixed(2))
        : undefined;
      subItem.amount = finalAmount ? Number(finalAmount.toFixed(2)) : undefined;
    }
  };

  const updateCategoryTotalsFromSubSplits = (
    jobId: string | number,
    catItem: CategorySplitItem
  ) => {
    if (catItem.subSplitItems.length > 1) {
      const totals = catItem.subSplitItems.reduce(
        (acc, sub) => ({
          quantity: acc.quantity + (Number(sub.quantity) || 0),
          percent: acc.percent + (Number(sub.percent) || 0),
          amount: acc.amount + (Number(sub.amount) || 0),
        }),
        { quantity: 0, percent: 0, amount: 0 }
      );

      const job = orderItems?.jobs?.find(
        (j) => String(j.jobId) === String(jobId)
      );
      const totalAmount =
        jobId === '-1' ? formTotal.orderTotal || 0 : job?.totalCharge || 0;
      const isPercentFullyAllocated = Math.abs(totals.percent - 100) < 0.01;

      let finalAmount = totals.amount;
      if (isPercentFullyAllocated && totalAmount > 0 && totals.quantity > 0) {
        const totalQty =
          jobId === '-1' ? orderTotalQuantity : job?.quantity || 0;
        if (totalQty > 0) {
          finalAmount = (totals.quantity / totalQty) * totalAmount;
        }
      }

      catItem.quantity = totals.quantity || undefined;
      catItem.percent = totals.percent
        ? Number(totals.percent.toFixed(2))
        : undefined;
      catItem.amount = finalAmount ? Number(finalAmount.toFixed(2)) : undefined;
    } else {
      const subItem = catItem.subSplitItems[0];
      if (subItem) {
        catItem.quantity = subItem.quantity;
        catItem.percent = subItem.percent;
        catItem.amount = subItem.amount;
      }
    }
  };

  const handleCategorySplitItemChange = async (
    jobId: string | number,
    id: string,
    field: string,
    value: any
  ) => {
    setLastChangedId(id);
    setValidationErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[id];
      return newErrors;
    });
    let fetchedConfigs: CostDistributionConfig[] = [];
    let catName: string | undefined;

    if (field === 'categoryId') {
      const category = categories.find((c) => String(c.id) === String(value));
      catName = category?.category;
      try {
        fetchedConfigs = await orderService.listCostDistributionConfigs(
          String(value)
        );
      } catch (error) {
        console.error('Failed to fetch cost distribution configs:', error);
      }
    }

    setUpdateDistributions(jobId, (prevItems) => {
      return prevItems.map((item) => {
        if (item.id !== id) return item;
        const updatedItem = { ...item, [field]: value };
        if (field === 'categoryId') {
          updatedItem.categoryName = catName;
          updatedItem.configs = fetchedConfigs;
          updatedItem.subSplitItems = [
            {
              id: uniqueId('split-'),
              configId: undefined,
              quantity: undefined,
              percent: undefined,
              amount: undefined,
              banners: [],
              isBannerSplitEnabled: false,
              bannerSplitItems: [
                {
                  id: uniqueId('banner-split-'),
                  bannerId: undefined,
                  quantity: undefined,
                  percent: undefined,
                  amount: undefined,
                },
              ],
            },
          ];
        }

        const job = orderItems?.jobs?.find(
          (j) => String(j.jobId) === String(jobId)
        );
        const totalQty =
          jobId === '-1' ? orderTotalQuantity : job?.quantity || 0;
        const totalAmount =
          jobId === '-1' ? formTotal.orderTotal || 0 : job?.totalCharge || 0;

        if (field === 'quantity' && totalQty > 0) {
          const quantity = value === undefined ? undefined : Number(value);
          if (quantity === undefined) {
            updatedItem.percent = undefined;
            updatedItem.amount = undefined;
          } else {
            const ratio = quantity / totalQty;
            updatedItem.percent = Number((ratio * 100).toFixed(2));
            updatedItem.amount =
              totalAmount > 0
                ? Number((ratio * totalAmount).toFixed(2))
                : undefined;
          }

          if (
            updatedItem.subSplitItems.length === 1 &&
            updatedItem.subSplitItems[0].bannerSplitItems.length === 1
          ) {
            updatedItem.subSplitItems[0].quantity = updatedItem.quantity;
            updatedItem.subSplitItems[0].percent = updatedItem.percent;
            updatedItem.subSplitItems[0].amount = updatedItem.amount;
          }
        } else if (field === 'percent' && (totalQty > 0 || totalAmount > 0)) {
          const percent = value === undefined ? undefined : Number(value);
          if (percent === undefined) {
            updatedItem.quantity = undefined;
            updatedItem.amount = undefined;
          } else {
            const ratio = percent / 100;
            updatedItem.quantity =
              totalQty > 0 ? Math.round(ratio * totalQty) : undefined;
            updatedItem.amount =
              totalAmount > 0
                ? Number((ratio * totalAmount).toFixed(2))
                : undefined;
          }

          if (
            updatedItem.subSplitItems.length === 1 &&
            updatedItem.subSplitItems[0].bannerSplitItems.length === 1
          ) {
            updatedItem.subSplitItems[0].quantity = updatedItem.quantity;
            updatedItem.subSplitItems[0].percent = updatedItem.percent;
            updatedItem.subSplitItems[0].amount = updatedItem.amount;
          }
        } else if (field === 'amount' && totalAmount > 0) {
          const amount = value === undefined ? undefined : Number(value);
          if (amount === undefined) {
            updatedItem.percent = undefined;
            updatedItem.quantity = undefined;
          } else {
            const ratio = amount / totalAmount;
            updatedItem.percent = Number((ratio * 100).toFixed(2));
            updatedItem.quantity =
              totalQty > 0 ? Math.round(ratio * totalQty) : undefined;
          }

          if (
            updatedItem.subSplitItems.length === 1 &&
            updatedItem.subSplitItems[0].bannerSplitItems.length === 1
          ) {
            updatedItem.subSplitItems[0].quantity = updatedItem.quantity;
            updatedItem.subSplitItems[0].percent = updatedItem.percent;
            updatedItem.subSplitItems[0].amount = updatedItem.amount;
          }
        }
        return updatedItem;
      });
    });
  };

  const handleRemoveCategorySubSplitItem = (
    jobId: string | number,
    catItemId: string,
    subSplitId: string
  ) => {
    setUpdateDistributions(jobId, (prevItems) =>
      prevItems.map((item) => {
        if (item.id !== catItemId) return item;
        if (item.subSplitItems.length <= 1) return item;
        const updatedItem = {
          ...item,
          subSplitItems: item.subSplitItems.filter((s) => s.id !== subSplitId),
        };
        // If only one subSplit remains, clear its quantity/percent/amount
        if (updatedItem.subSplitItems.length === 1) {
          updatedItem.subSplitItems[0].quantity = undefined;
          updatedItem.subSplitItems[0].percent = undefined;
          updatedItem.subSplitItems[0].amount = undefined;
        }
        updateCategoryTotalsFromSubSplits(jobId, updatedItem);
        return updatedItem;
      })
    );
  };

  const handleCategorySubSplitItemChange = async (
    jobId: string | number,
    catItemId: string,
    subSplitId: string,
    field: string,
    value: any
  ) => {
    setLastChangedId(subSplitId);
    setUpdateDistributions(jobId, (prevItems) => {
      return prevItems.map((catItem) => {
        if (catItem.id !== catItemId) return catItem;

        const updatedSubSplitItems = catItem.subSplitItems.map((subItem) => {
          if (subItem.id !== subSplitId) return subItem;

          const updatedSubItem = { ...subItem, [field]: value };

          if (field !== 'configId') {
            const job = orderItems?.jobs?.find(
              (j) => String(j.jobId) === String(jobId)
            );
            const totalQty =
              jobId === '-1' ? orderTotalQuantity : job?.quantity || 0;
            const totalAmount =
              jobId === '-1'
                ? formTotal.orderTotal || 0
                : job?.totalCharge || 0;

            if (field === 'quantity' && totalQty > 0) {
              const quantity = value === undefined ? undefined : Number(value);
              if (quantity === undefined) {
                updatedSubItem.percent = undefined;
                updatedSubItem.amount = undefined;
              } else {
                const ratio = quantity / totalQty;
                updatedSubItem.percent = Number((ratio * 100).toFixed(2));
                updatedSubItem.amount =
                  totalAmount > 0
                    ? Number((ratio * totalAmount).toFixed(2))
                    : undefined;
              }
            } else if (
              field === 'percent' &&
              (totalQty > 0 || totalAmount > 0)
            ) {
              const percent = value === undefined ? undefined : Number(value);
              if (percent === undefined) {
                updatedSubItem.quantity = undefined;
                updatedSubItem.amount = undefined;
              } else {
                const ratio = percent / 100;
                updatedSubItem.quantity =
                  totalQty > 0 ? Math.round(ratio * totalQty) : undefined;
                updatedSubItem.amount =
                  totalAmount > 0
                    ? Number((ratio * totalAmount).toFixed(2))
                    : undefined;
              }
            } else if (field === 'amount' && totalAmount > 0) {
              const amount = value === undefined ? undefined : Number(value);
              if (amount === undefined) {
                updatedSubItem.percent = undefined;
                updatedSubItem.quantity = undefined;
              } else {
                const ratio = amount / totalAmount;
                updatedSubItem.percent = Number((ratio * 100).toFixed(2));
                updatedSubItem.quantity =
                  totalQty > 0 ? Math.round(ratio * totalQty) : undefined;
              }
            }
          }
          return updatedSubItem;
        });

        const updatedItem = {
          ...catItem,
          subSplitItems: updatedSubSplitItems,
        };

        if (field !== 'configId') {
          updateCategoryTotalsFromSubSplits(jobId, updatedItem);
        }

        return updatedItem;
      });
    });

    if (field === 'configId') {
      const catItem = getItemsByJobId(jobId).find((i) => i.id === catItemId);
      if (catItem && hasBannerSupport(catItem.categoryName)) {
        const selectedConfig = catItem.configs.find((c) => c.id === value);
        if (selectedConfig) {
          try {
            const bannerList = await orderService.queryBanners(
              selectedConfig.site,
              selectedConfig.siteDesc
            );
            setUpdateDistributions(jobId, (prevItems) =>
              prevItems.map((cat) => {
                if (cat.id !== catItemId) return cat;
                return {
                  ...cat,
                  subSplitItems: cat.subSplitItems.map((sub) => {
                    if (sub.id !== subSplitId) return sub;
                    return {
                      ...sub,
                      banners: bannerList,
                      bannerSplitItems:
                        bannerList.length > 0
                          ? [
                              {
                                id: uniqueId('banner-split-'),
                                bannerId:
                                  bannerList.length === 1
                                    ? bannerList[0].id
                                    : undefined,
                                quantity: undefined,
                                percent: undefined,
                                amount: undefined,
                              },
                            ]
                          : [],
                    };
                  }),
                };
              })
            );
          } catch (error) {
            console.error('Failed to fetch banners:', error);
          }
        }
      }
    }
  };

  const handleCategorySubSplitBannerItemChange = (
    jobId: string | number,
    catItemId: string,
    subSplitId: string,
    bannerSplitId: string,
    field: string,
    value: any
  ) => {
    setLastChangedId(bannerSplitId);
    setUpdateDistributions(jobId, (prevItems) => {
      return prevItems.map((catItem) => {
        if (catItem.id !== catItemId) return catItem;

        const updatedSubSplitItems = catItem.subSplitItems.map((subItem) => {
          if (subItem.id !== subSplitId) return subItem;

          const updatedBannerSplitItems = subItem.bannerSplitItems.map(
            (bannerItem) => {
              if (bannerItem.id !== bannerSplitId) return bannerItem;

              const updatedBannerItem = { ...bannerItem, [field]: value };

              if (field !== 'bannerId') {
                const job = orderItems?.jobs?.find(
                  (j) => String(j.jobId) === String(jobId)
                );
                const totalQty =
                  jobId === '-1' ? orderTotalQuantity : job?.quantity || 0;
                const totalAmount =
                  jobId === '-1'
                    ? formTotal.orderTotal || 0
                    : job?.totalCharge || 0;

                if (field === 'quantity' && totalQty > 0) {
                  const quantity =
                    value === undefined ? undefined : Number(value);
                  if (quantity === undefined) {
                    updatedBannerItem.percent = undefined;
                    updatedBannerItem.amount = undefined;
                  } else {
                    const ratio = quantity / totalQty;
                    updatedBannerItem.percent = Number((ratio * 100).toFixed(2));
                    updatedBannerItem.amount =
                      totalAmount > 0
                        ? Number((ratio * totalAmount).toFixed(2))
                        : undefined;
                  }
                } else if (
                  field === 'percent' &&
                  (totalQty > 0 || totalAmount > 0)
                ) {
                  const percent =
                    value === undefined ? undefined : Number(value);
                  if (percent === undefined) {
                    updatedBannerItem.quantity = undefined;
                    updatedBannerItem.amount = undefined;
                  } else {
                    const ratio = percent / 100;
                    updatedBannerItem.quantity =
                      totalQty > 0 ? Math.round(ratio * totalQty) : undefined;
                    updatedBannerItem.amount =
                      totalAmount > 0
                        ? Number((ratio * totalAmount).toFixed(2))
                        : undefined;
                  }
                } else if (field === 'amount' && totalAmount > 0) {
                  const amount =
                    value === undefined ? undefined : Number(value);
                  if (amount === undefined) {
                    updatedBannerItem.percent = undefined;
                    updatedBannerItem.quantity = undefined;
                  } else {
                    const ratio = amount / totalAmount;
                    updatedBannerItem.percent = Number((ratio * 100).toFixed(2));
                    updatedBannerItem.quantity =
                      totalQty > 0 ? Math.round(ratio * totalQty) : undefined;
                  }
                }
              }
              return updatedBannerItem;
            }
          );

          const updatedSubItem = {
            ...subItem,
            bannerSplitItems: updatedBannerSplitItems,
          };

          if (field !== 'bannerId') {
            updateSubSplitTotalsFromBanners(jobId, updatedSubItem);
          }

          return updatedSubItem;
        });

        const updatedCatItem = {
          ...catItem,
          subSplitItems: updatedSubSplitItems,
        };

        if (field !== 'bannerId') {
          updateCategoryTotalsFromSubSplits(jobId, updatedCatItem);
        }

        return updatedCatItem;
      });
    });
  };

  const handleRemoveCategorySubSplitBannerItem = (
    jobId: string | number,
    catItemId: string,
    subSplitId: string,
    bannerSplitId: string
  ) => {
    setUpdateDistributions(jobId, (prevItems) => {
      return prevItems.map((catItem) => {
        if (catItem.id !== catItemId) return catItem;

        const updatedCatItem = {
          ...catItem,
          subSplitItems: catItem.subSplitItems.map((subItem) => {
            if (subItem.id !== subSplitId) return subItem;
            if (subItem.bannerSplitItems.length <= 1) return subItem;
            const updatedSubItem = {
              ...subItem,
              bannerSplitItems: subItem.bannerSplitItems.filter(
                (b) => b.id !== bannerSplitId
              ),
            };
            // If only one banner remains, clear its quantity/percent/amount
            if (updatedSubItem.bannerSplitItems.length === 1) {
              updatedSubItem.bannerSplitItems[0].quantity = undefined;
              updatedSubItem.bannerSplitItems[0].percent = undefined;
              updatedSubItem.bannerSplitItems[0].amount = undefined;

              // Clear subSplit values
              updatedSubItem.quantity = undefined;
              updatedSubItem.percent = undefined;
              updatedSubItem.amount = undefined;

              // If there's only one subSplit, also clear category values
              if (catItem.subSplitItems.length === 1) {
                catItem.quantity = undefined;
                catItem.percent = undefined;
                catItem.amount = undefined;
              }
            } else {
              // Only update totals if there are multiple banners
              updateSubSplitTotalsFromBanners(jobId, updatedSubItem);
            }
            return updatedSubItem;
          }),
        };

        return updatedCatItem;
      });
    });
  };

  const globalHasOverAllocation = useMemo(() => {
    const jobIds = isApplyToAllJobs
      ? ['-1']
      : orderItems?.jobs?.map((j) => String(j.jobId)) || [];
    return jobIds.some((jid) => getJobCalculations(jid).hasOverAllocation);
  }, [isApplyToAllJobs, orderItems?.jobs, getJobCalculations]);

  useEffect(() => {
    if (!globalHasOverAllocation) {
      setLastChangedId(undefined);
    }
  }, [globalHasOverAllocation]);

  // A billing contact profile can only store a single billing code. Count the
  // billing codes produced by the current cost distribution so we can warn the
  // user when they try to save multiple codes to the contact profile.
  const hasMultipleBillingCodes = useMemo(() => {
    const activeJobIds = isApplyToAllJobs
      ? ['-1']
      : orderItems?.jobs?.map((j) => String(j.jobId)) || [];

    let count = 0;
    for (const jid of activeJobIds) {
      const items = getItemsByJobId(jid);
      for (const cat of items) {
        if (!cat.categoryId) continue;
        for (const sub of cat.subSplitItems) {
          if (!sub.configId) continue;
          const selectedBanners = sub.bannerSplitItems.filter(
            (b) => b.bannerId && b.bannerId !== -1
          );
          count += selectedBanners.length > 0 ? selectedBanners.length : 1;
          if (count > 1) return true;
        }
      }
    }
    return false;
  }, [isApplyToAllJobs, orderItems?.jobs, getItemsByJobId]);

  if (!(org?.id && CHECKOUT_ALBERTSONS_ORGS.includes(org.id))) {
    return null;
  }

  const accountTypeList = [
    {
      name: 'Organization',
      value: ShippingAccountTypeEnum.ORGANIZATION,
    },
    {
      name: 'Revshop',
      value: ShippingAccountTypeEnum.REVSHOP,
    },
    {
      name: 'Supplier',
      value: ShippingAccountTypeEnum.SUPPLIER,
    },
  ];

  const canShowProfileOptions =
    !hideProfileOptions &&
    (isApplyToAllJobs || (orderItems?.jobs && orderItems.jobs.length === 1));

  const updateBillingContactCheckbox = (
    <div>
      <Checkbox
        checked={updateContactOracleBillingCode}
        onChange={(e) => setUpdateContactOracleBillingCode(e.target.checked)}
      >
        Update Billing Contact with the selected Cost Distribution values for
        future orders.
      </Checkbox>
      {updateContactOracleBillingCode && hasMultipleBillingCodes && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            marginTop: '4px',
          }}
        >
          <InfoCircleFilled style={{ color: '#faad14' }} />
          <Typography.Text type="warning" style={{ fontSize: '12px' }}>
            Only single billing codes can be stored in a billing contacts
            profile
          </Typography.Text>
        </div>
      )}
    </div>
  );

  const renderDistributionBlock = (jobId: string | number) => {
    const items = getItemsByJobId(jobId);
    const { totals, hasOverAllocation, isSplitActive, totalQty, totalAmount } =
      getJobCalculations(jobId);
    const job = orderItems?.jobs?.find(
      (j) => String(j.jobId) === String(jobId)
    );

    const shippingItem = job?.shippingItems?.[0];

    // If quantity matches totalQty, display percent as 100% and amount as totalAmount
    const displayPercent = totals.quantity === totalQty ? 100 : totals.percent;
    const displayAmount =
      totals.quantity === totalQty ? totalAmount : totals.amount;

    return (
      <div
        key={String(jobId)}
        style={{
          marginBottom: '24px',
          padding: '16px 12px',
          border: '1px solid #e8e8e8',
          borderRadius: '8px',
          background: '#fff',
        }}
      >
        {!isApplyToAllJobs && job && (
          <div
            style={{
              display: 'flex',
              padding: '12px',
              background: '#fafafa',
              borderLeft: '4px solid #009088',
              marginBottom: '16px',
              gap: '12px',
              alignItems: 'flex-start',
            }}
          >
            {/* Job Name */}
            <div style={{ flex: '0 0 140px' }}>
              <SectionHeading>{job.jobName}</SectionHeading>
            </div>

            {/* Quantity */}
            <div
              style={{
                flex: '0 0 80px',
                borderLeft: '1px solid #f0f0f0',
                paddingLeft: '12px',
              }}
            >
              <div
                style={{
                  fontSize: '11px',
                  color: '#8c8c8c',
                  marginBottom: '2px',
                }}
              >
                Quantity:
              </div>
              <SectionHeading>{job.quantity}</SectionHeading>
            </div>

            {/* Address */}
            <div
              style={{
                flex: '1',
                borderLeft: '1px solid #f0f0f0',
                paddingLeft: '12px',
              }}
            >
              <div
                style={{
                  fontSize: '11px',
                  color: '#8c8c8c',
                  marginBottom: '2px',
                }}
              >
                Send to Name & Address:
              </div>
              <TextHeavy>
                {shippingItem?.shippingInfo?.toName}
                <br />
                {[
                  shippingItem?.shippingInfo?.toAddressLine,
                  shippingItem?.shippingInfo?.toCity,
                  shippingItem?.shippingInfo?.toStateProvinceCode,
                  shippingItem?.shippingInfo?.toPostalCode,
                  shippingItem?.shippingInfo?.toCountryCode,
                ]
                  .filter(Boolean)
                  .join(', ')}
              </TextHeavy>
            </div>

            {/* Deliver By */}
            <div
              style={{
                flex: '0 0 140px',
                borderLeft: '1px solid #f0f0f0',
                paddingLeft: '12px',
              }}
            >
              <div
                style={{
                  fontSize: '11px',
                  color: '#8c8c8c',
                  marginBottom: '2px',
                }}
              >
                Deliver By:
              </div>
              <TextHeavy>
                {shippingItem?.carrier?.estimatedDeliveryTime ? (
                  <>
                    <p style={{ marginBottom: 0 }}>
                      <RevxDateWeek
                        dateStr={shippingItem.carrier.estimatedDeliveryTime}
                      />
                    </p>
                    <p style={{ marginBottom: 0 }}>
                      <RevxDate
                        dateStr={shippingItem.carrier.estimatedDeliveryTime}
                      />
                    </p>
                  </>
                ) : null}
                <p style={{ marginBottom: 0 }}>
                  {[
                    shippingItem?.carrier?.carrierType,
                    shippingItem?.carrier?.carrierService,
                    accountTypeList.find(
                      (item) =>
                        item.value === shippingItem?.carrier?.accountType
                    )?.name,
                  ]
                    .filter((item) => item)
                    .join(', ')}
                </p>
              </TextHeavy>
            </div>

            {/* Totals */}
            <div
              style={{
                flex: '0 0 160px',
                borderLeft: '1px solid #f0f0f0',
                paddingLeft: '12px',
                textAlign: 'right',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: '11px',
                  color: '#595959',
                }}
              >
                <span>Printing:</span>
                <TextHeavy>
                  <RevxMoney value={job.printingCharge ?? 0} />
                </TextHeavy>
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: '11px',
                  color: '#595959',
                  marginBottom: '2px',
                }}
              >
                <span>Freight:</span>
                <TextHeavy>
                  <RevxMoney value={job.freightCharge ?? 0} />
                </TextHeavy>
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                }}
              >
                <span style={{ fontSize: '12px', fontWeight: 600 }}>
                  Subtotal:
                </span>
                <span
                  style={{
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#262626',
                  }}
                >
                  <RevxMoney value={job.totalCharge ?? 0} />
                </span>
              </div>
            </div>
          </div>
        )}

        <Space direction="vertical" style={{ width: '100%' }} size={8}>
          <div
            style={{
              display: 'flex',
              gap: '8px', // Reduced from 8px
              alignItems: 'flex-end',
              paddingLeft: '0px',
              height: '20px',
              marginBottom: '4px',
            }}
          >
            <div style={{ width: '160px' }}>
              <Typography.Text strong style={{ fontSize: '12px' }}>
                Cost Distribution Category:
              </Typography.Text>
            </div>
            {items.length > 1 && (
              <div style={{ display: 'flex', gap: '7px' }}>
                <Typography.Text
                  style={{
                    paddingLeft: '18px',
                    width: '45px',
                    fontSize: '11px',
                  }}
                >
                  Qty
                </Typography.Text>
              </div>
            )}
          </div>

          {items.map((catItem, catIndex) => (
            <div
              key={catItem.id}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                backgroundColor: catIndex % 2 === 0 ? '#fafafa' : '#ffffff',
                padding: '7px', // Reduced from 12px
                borderRadius: '8px',
                border: '1px solid #f0f0f0',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  gap: '7px', // Reduced from 8px
                  alignItems: 'flex-start',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '160px',
                  }}
                >
                  <div style={{ height: '20px', marginBottom: '4px' }} />
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      height: '32px',
                    }}
                  >
                    <Select
                      placeholder="Select category"
                      style={{
                        width: '100%',
                        ...(isFieldError(catItem.id, 'categoryId')
                          ? errorSelectStyle
                          : {}),
                      }}
                      dropdownMatchSelectWidth={false}
                      value={
                        catItem.categoryId
                          ? String(catItem.categoryId)
                          : undefined
                      }
                      disabled={isManualOracleString}
                      showSearch
                      filterOption={(input, option) =>
                        (option?.children as string)
                          ?.toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      onChange={(val) => {
                        handleCategorySplitItemChange(
                          jobId,
                          catItem.id,
                          'categoryId',
                          val
                        );
                        clearFieldError(catItem.id, 'categoryId');
                      }}
                    >
                      {categories.map((category) => (
                        <Select.Option
                          key={category.id}
                          value={String(category.id)}
                          disabled={items.some(
                            (ci) =>
                              String(ci.categoryId) === String(category.id) &&
                              ci.id !== catItem.id
                          )}
                        >
                          {category.category}
                        </Select.Option>
                      ))}
                    </Select>
                  </div>
                </div>

                {items.length > 1 && (
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <div style={{ height: '20px', marginBottom: '4px' }} />
                    <div
                      style={{
                        display: 'flex',
                        gap: '2px', // Reduced from 4px
                        alignItems: 'center',
                        height: '32px',
                      }}
                    >
                      <Input
                        style={{
                          width: '45px',
                          paddingLeft: '2px',
                          paddingRight: '2px',
                          textAlign: 'center',
                          ...(isFieldError(catItem.id, 'quantity') ||
                          (hasOverAllocation && lastChangedId === catItem.id)
                            ? errorInputStyle
                            : {}),
                        }}
                        value={catItem.quantity ?? ''}
                        disabled={isManualOracleString}
                        onChange={(e) => {
                          handleCategorySplitItemChange(
                            jobId,
                            catItem.id,
                            'quantity',
                            parseNumberInput(e.target.value)
                          );
                          clearFieldError(catItem.id, 'quantity');
                          clearFieldError(catItem.id, 'percent');
                          clearFieldError(catItem.id, 'amount');
                        }}
                      />
                      <Input
                        style={{
                          width: '55px',
                          paddingLeft: '2px',
                          paddingRight: '2px',
                          textAlign: 'center',
                          ...(isFieldError(catItem.id, 'percent') ||
                          (hasOverAllocation && lastChangedId === catItem.id)
                            ? errorInputStyle
                            : {}),
                        }}
                        value={catItem.percent ?? ''}
                        suffix="%"
                        disabled={isManualOracleString}
                        onChange={(e) => {
                          handleCategorySplitItemChange(
                            jobId,
                            catItem.id,
                            'percent',
                            parseNumberInput(e.target.value)
                          );
                          clearFieldError(catItem.id, 'quantity');
                          clearFieldError(catItem.id, 'percent');
                          clearFieldError(catItem.id, 'amount');
                        }}
                      />
                      <Input
                        style={{
                          width: '65px',
                          paddingLeft: '2px',
                          paddingRight: '2px',
                          textAlign: 'center',
                          ...(isFieldError(catItem.id, 'amount') ||
                          (hasOverAllocation && lastChangedId === catItem.id)
                            ? errorInputStyle
                            : {}),
                        }}
                        value={catItem.amount ?? ''}
                        prefix="$"
                        disabled={isManualOracleString}
                        onChange={(e) => {
                          handleCategorySplitItemChange(
                            jobId,
                            catItem.id,
                            'amount',
                            parseNumberInput(e.target.value)
                          );
                          clearFieldError(catItem.id, 'quantity');
                          clearFieldError(catItem.id, 'percent');
                          clearFieldError(catItem.id, 'amount');
                        }}
                      />
                    </div>
                  </div>
                )}

                {items.length > 1 && (
                  <div
                    style={{
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      marginTop: '24px',
                      marginLeft: '0px', // Reduced from -4px
                    }}
                  >
                    <RevxButton
                      type="text"
                      size="small"
                      disabled={isManualOracleString}
                      onClick={() =>
                        handleRemoveCategorySplitItem(jobId, catItem.id)
                      }
                      style={{
                        width: '24px',
                        height: '24px',
                        padding: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#fff',
                        border: '1px solid #d9d9d9',
                        borderRadius: '50%',
                      }}
                    >
                      <DeleteOutlined
                        style={{ fontSize: '11px', color: '#8c8c8c' }}
                      />
                    </RevxButton>
                  </div>
                )}

                <div
                  style={{
                    flex: 1,
                    marginLeft: '2px', // Reduced from 4px
                    paddingLeft: '4px', // Reduced from 8px
                    borderLeft: '1px solid #e8e8e8',
                  }}
                >
                  <Space
                    direction="vertical"
                    style={{ width: '100%' }}
                    size={4} // Reduced from 8
                  >
                    {catItem.subSplitItems.map((subItem, subIndex) => (
                      <div
                        key={subItem.id}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '4px',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            gap: '8px',
                            alignItems: 'flex-start',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              minWidth: '160px',
                              flex: '0 0 auto',
                            }}
                          >
                            <div
                              style={{
                                height:
                                  subIndex === 0
                                    ? '20px'
                                    : hasBannerSupport(catItem.categoryName)
                                    ? '20px'
                                    : '5px',
                                marginBottom:
                                  subIndex === 0
                                    ? '4px'
                                    : hasBannerSupport(catItem.categoryName)
                                    ? '4px'
                                    : '2px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                              }}
                            >
                              {subIndex === 0 && (
                                <Typography.Text
                                  strong
                                  style={{ fontSize: '12px' }}
                                >
                                  {catItem.categoryName === 'Corporate'
                                    ? 'Cost Center'
                                    : 'Site'}
                                </Typography.Text>
                              )}
                            </div>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                height: '32px',
                                gap: '8px',
                              }}
                            >
                              <Select
                                placeholder={
                                  catItem.categoryName === 'Corporate'
                                    ? 'Select cost center'
                                    : 'Select site'
                                }
                                style={{
                                  width: '160px',
                                  ...(isFieldError(subItem.id, 'configId')
                                    ? errorSelectStyle
                                    : {}),
                                }}
                                dropdownMatchSelectWidth={false}
                                value={subItem.configId}
                                disabled={
                                  !catItem.categoryId || isManualOracleString
                                }
                                showSearch
                                filterOption={(input, option) =>
                                  ((option?.label as string) || '')
                                    .toLowerCase()
                                    .includes(input.toLowerCase())
                                }
                                onChange={(val) => {
                                  handleCategorySubSplitItemChange(
                                    jobId,
                                    catItem.id,
                                    subItem.id,
                                    'configId',
                                    val
                                  );
                                  clearFieldError(subItem.id, 'configId');
                                }}
                              >
                                {catItem.configs.map((config) => {
                                  const isCorporate =
                                    catItem.categoryName === 'Corporate';
                                  const shouldShowSiteDesc = [
                                    'Retail Admin',
                                    'Division Admin',
                                    'Retail Admin-eCom',
                                    'Store Allocation',
                                    'Distribution Center',
                                    'Plant Production',
                                  ].includes(catItem.categoryName || '');

                                  const code = isCorporate
                                    ? config.costCenter
                                    : config.site;
                                  const desc =
                                    config.description ||
                                    (isCorporate
                                      ? config.costCenterDesc
                                      : config.siteDesc);
                                  const labelStr = formatConfigDesc(
                                    desc,
                                    catItem.categoryName,
                                    code
                                  );
                                  return (
                                    <Select.Option
                                      key={config.id}
                                      value={config.id}
                                      label={labelStr}
                                      disabled={catItem.subSplitItems.some(
                                        (si) =>
                                          si.configId === config.id &&
                                          si.id !== subItem.id
                                      )}
                                    >
                                      <Tooltip
                                        title={desc}
                                        placement="topLeft"
                                        overlayStyle={{ maxWidth: 'none' }}
                                        overlayInnerStyle={{
                                          whiteSpace: 'nowrap',
                                        }}
                                      >
                                        <div
                                          style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                          }}
                                        >
                                          <Typography.Text
                                            style={{ color: '#262626', lineHeight: '1.4' }}
                                          >
                                            {isCorporate &&
                                            config.costCenterDesc
                                              ? `${code} ${config.costCenterDesc}`
                                              : shouldShowSiteDesc &&
                                                config.siteDesc
                                              ? `${code} ${config.siteDesc}`
                                              : config.banner
                                              ? `${code} ${config.banner}`
                                              : code}
                                          </Typography.Text>

                                          <div
                                            style={{
                                              fontSize: '11px',
                                              color: '#595959',
                                              lineHeight: '1.2',
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                              whiteSpace: 'nowrap',
                                            }}
                                          >
                                            {desc}
                                          </div>
                                        </div>
                                      </Tooltip>
                                    </Select.Option>
                                  );
                                })}
                              </Select>

                              {/* Oracle String icon - show for Site/Cost Center */}
                              {(() => {
                                let oracleKey = `${jobId}-${catItem.id}-${subItem.id}`;
                                let oracleStr = oracleStrings[oracleKey];

                                // If has banners, use the first banner's oracle string
                                if (
                                  subItem.banners?.length > 0 &&
                                  subItem.bannerSplitItems.length > 0
                                ) {
                                  const firstBanner =
                                    subItem.bannerSplitItems[0];
                                  if (
                                    firstBanner.bannerId &&
                                    firstBanner.bannerId !== -1
                                  ) {
                                    const bannerOracleKey = `${jobId}-${catItem.id}-${subItem.id}-${firstBanner.id}`;
                                    const bannerOracleStr =
                                      oracleStrings[bannerOracleKey];
                                    if (bannerOracleStr) {
                                      oracleStr = bannerOracleStr;
                                    }
                                  }
                                }

                                console.log(
                                  'Site/Cost Center - oracleKey:',
                                  oracleKey,
                                  'oracleStr:',
                                  oracleStr
                                );

                                return (
                                  <Tooltip
                                    title={
                                      oracleStr ? (
                                        <div
                                          style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            whiteSpace: 'nowrap',
                                          }}
                                        >
                                          <span>{oracleStr}</span>
                                          <RevxButton
                                            type="primary"
                                            size="small"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              copyToClipboard(oracleStr);
                                            }}
                                            style={{
                                              fontSize: '11px',
                                              padding: '0 8px',
                                              height: '22px',
                                            }}
                                          >
                                            Copy
                                          </RevxButton>
                                        </div>
                                      ) : (
                                        'No Billing Code yet'
                                      )
                                    }
                                    placement="top"
                                    overlayStyle={{
                                      zIndex: 99999,
                                      maxWidth: 'none',
                                    }}
                                    overlayInnerStyle={{
                                      maxWidth: 'none',
                                      whiteSpace: 'nowrap',
                                    }}
                                    getPopupContainer={() => document.body}
                                  >
                                    <InfoCircleFilled
                                      style={{
                                        fontSize: '14px',
                                        color: oracleStr
                                          ? '#1890ff'
                                          : '#d9d9d9',
                                        cursor: oracleStr
                                          ? 'pointer'
                                          : 'default',
                                        flexShrink: 0,
                                      }}
                                    />
                                  </Tooltip>
                                );
                              })()}
                            </div>

                            {/* Add Site/Cost Center button row */}
                            {subIndex === catItem.subSplitItems.length - 1 &&
                              catItem.subSplitItems.length <
                                catItem.configs.length && (
                                <div
                                  style={{
                                    marginTop: subIndex === 0 ? '4px' : '2px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    width: '160px',
                                  }}
                                >
                                  <Typography.Link
                                    style={{
                                      fontSize: '11px',
                                      marginLeft: 'auto',
                                    }}
                                    disabled={isManualOracleString}
                                    onClick={() =>
                                      !isManualOracleString &&
                                      handleAddCategorySubSplitItem(
                                        jobId,
                                        catItem.id
                                      )
                                    }
                                  >
                                    +{' '}
                                    {catItem.categoryName === 'Corporate'
                                      ? 'Cost Center'
                                      : 'Site'}
                                  </Typography.Link>
                                </div>
                              )}
                          </div>

                          {catItem.subSplitItems.length > 1 && (
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                              }}
                            >
                              <div
                                style={{
                                  height:
                                    subIndex === 0
                                      ? '20px'
                                      : hasBannerSupport(catItem.categoryName)
                                      ? '20px'
                                      : '5px',
                                  marginBottom:
                                    subIndex === 0
                                      ? '4px'
                                      : hasBannerSupport(catItem.categoryName)
                                      ? '4px'
                                      : '2px',
                                }}
                              >
                                {subIndex === 0 && (
                                  <div style={{ display: 'flex', gap: '8px' }}>
                                    <Typography.Text
                                      style={{
                                        width: '45px',
                                        fontSize: '11px',
                                      }}
                                    >
                                      Qty
                                    </Typography.Text>
                                  </div>
                                )}
                              </div>
                              <div
                                style={{
                                  display: 'flex',
                                  gap: '2px', // Reduced from 4px
                                  alignItems: 'center',
                                  height: '32px',
                                }}
                              >
                                <Input
                                  style={{
                                    width: '45px',
                                    paddingLeft: '2px',
                                    paddingRight: '2px',
                                    textAlign: 'center',
                                    ...(isFieldError(subItem.id, 'quantity') ||
                                    (hasOverAllocation &&
                                      lastChangedId === subItem.id)
                                      ? errorInputStyle
                                      : {}),
                                  }}
                                  value={subItem.quantity ?? ''}
                                  disabled={isManualOracleString}
                                  onChange={(e) => {
                                    handleCategorySubSplitItemChange(
                                      jobId,
                                      catItem.id,
                                      subItem.id,
                                      'quantity',
                                      parseNumberInput(e.target.value)
                                    );
                                    clearFieldError(subItem.id, 'quantity');
                                  }}
                                />
                                <Input
                                  style={{
                                    width: '55px',
                                    paddingLeft: '2px',
                                    paddingRight: '2px',
                                    textAlign: 'center',
                                    ...(isFieldError(subItem.id, 'percent') ||
                                    (hasOverAllocation &&
                                      lastChangedId === subItem.id)
                                      ? errorInputStyle
                                      : {}),
                                  }}
                                  value={subItem.percent ?? ''}
                                  suffix="%"
                                  disabled={isManualOracleString}
                                  onChange={(e) => {
                                    handleCategorySubSplitItemChange(
                                      jobId,
                                      catItem.id,
                                      subItem.id,
                                      'percent',
                                      parseNumberInput(e.target.value)
                                    );
                                    clearFieldError(subItem.id, 'percent');
                                  }}
                                />
                                <Input
                                  style={{
                                    width: '65px',
                                    paddingLeft: '2px',
                                    paddingRight: '2px',
                                    textAlign: 'center',
                                    ...(isFieldError(subItem.id, 'amount') ||
                                    (hasOverAllocation &&
                                      lastChangedId === subItem.id)
                                      ? errorInputStyle
                                      : {}),
                                  }}
                                  value={subItem.amount ?? ''}
                                  prefix="$"
                                  disabled={isManualOracleString}
                                  onChange={(e) => {
                                    handleCategorySubSplitItemChange(
                                      jobId,
                                      catItem.id,
                                      subItem.id,
                                      'amount',
                                      parseNumberInput(e.target.value)
                                    );
                                    clearFieldError(subItem.id, 'amount');
                                  }}
                                />
                              </div>
                            </div>
                          )}

                          {catItem.subSplitItems.length > 1 && (
                            <div
                              style={{
                                height: '32px',
                                display: 'flex',
                                alignItems: 'center',
                                marginTop:
                                  subIndex === 0
                                    ? '24px'
                                    : hasBannerSupport(catItem.categoryName)
                                    ? '24px'
                                    : '7px',
                                marginLeft: '0px', // Reduced from -4px
                              }}
                            >
                              <RevxButton
                                type="text"
                                size="small"
                                disabled={isManualOracleString}
                                onClick={() =>
                                  handleRemoveCategorySubSplitItem(
                                    jobId,
                                    catItem.id,
                                    subItem.id
                                  )
                                }
                                style={{
                                  width: '24px',
                                  height: '24px',
                                  padding: 0,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: '#fff',
                                  border: '1px solid #d9d9d9',
                                  borderRadius: '50%',
                                }}
                              >
                                <DeleteOutlined
                                  style={{ fontSize: '11px', color: '#8c8c8c' }}
                                />
                              </RevxButton>
                            </div>
                          )}

                          {hasBannerSupport(catItem.categoryName) &&
                            subItem.banners?.length > 0 && (
                              <div
                                style={{
                                  flex: 1,
                                  paddingLeft: '4px',
                                  borderLeft: '1px solid #f0f0f0',
                                }}
                              >
                                <Space
                                  direction="vertical"
                                  style={{ width: '100%' }}
                                  size={2}
                                >
                                  {subItem.bannerSplitItems.map(
                                    (bannerItem, bIndex) => (
                                      <div
                                        key={bannerItem.id}
                                        style={{
                                          display: 'flex',
                                          gap: '8px', // Reduced from 8px
                                          alignItems: 'flex-start',
                                        }}
                                      >
                                        <div
                                          style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            width: '140px',
                                          }}
                                        >
                                          <div
                                            style={{
                                              height:
                                                bIndex === 0
                                                  ? subIndex === 0
                                                    ? '20px'
                                                    : hasBannerSupport(
                                                        catItem.categoryName
                                                      )
                                                    ? '20px'
                                                    : '5px'
                                                  : '5px',
                                              marginBottom:
                                                bIndex === 0
                                                  ? subIndex === 0
                                                    ? '4px'
                                                    : hasBannerSupport(
                                                        catItem.categoryName
                                                      )
                                                    ? '4px'
                                                    : '2px'
                                                  : '2px',
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: '4px',
                                            }}
                                          >
                                            {bIndex === 0 && (
                                              <Typography.Text
                                                strong
                                                style={{ fontSize: '12px' }}
                                              >
                                                Banner (Optional)
                                              </Typography.Text>
                                            )}
                                          </div>
                                          <div
                                            style={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              height: '32px',
                                            }}
                                          >
                                            <Select
                                              placeholder="Select banner"
                                              style={{
                                                width: '140px',
                                                ...(isFieldError(
                                                  bannerItem.id,
                                                  'bannerId'
                                                )
                                                  ? errorSelectStyle
                                                  : {}),
                                              }}
                                              dropdownMatchSelectWidth={false}
                                              value={bannerItem.bannerId}
                                              disabled={isManualOracleString}
                                              onChange={(val) => {
                                                handleCategorySubSplitBannerItemChange(
                                                  jobId,
                                                  catItem.id,
                                                  subItem.id,
                                                  bannerItem.id,
                                                  'bannerId',
                                                  val
                                                );
                                                clearFieldError(
                                                  bannerItem.id,
                                                  'bannerId'
                                                );
                                              }}
                                            >
                                              <Select.Option
                                                key={-1}
                                                value={-1}
                                                disabled={
                                                  subItem.bannerSplitItems
                                                    .length > 1
                                                }
                                              >
                                                ---
                                              </Select.Option>
                                              {subItem.banners.map((b) => (
                                                <Select.Option
                                                  key={b.id}
                                                  value={b.id}
                                                  disabled={subItem.bannerSplitItems.some(
                                                    (bi) =>
                                                      bi.bannerId === b.id &&
                                                      bi.id !== bannerItem.id
                                                  )}
                                                >
                                                  {b.banner}
                                                </Select.Option>
                                              ))}
                                            </Select>
                                          </div>

                                          {/* Add Banner button row */}
                                          {bIndex ===
                                            subItem.bannerSplitItems.length -
                                              1 &&
                                            subItem.bannerSplitItems.length <
                                              subItem.banners.length &&
                                            !subItem.bannerSplitItems.some(
                                              (bi) => bi.bannerId === -1
                                            ) && (
                                              <div
                                                style={{
                                                  marginTop:
                                                    bIndex === 0
                                                      ? '4px'
                                                      : '2px',
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  width: '100%',
                                                }}
                                              >
                                                <Typography.Link
                                                  style={{
                                                    fontSize: '11px',
                                                    marginLeft: 'auto',
                                                  }}
                                                  disabled={
                                                    isManualOracleString
                                                  }
                                                  onClick={() =>
                                                    !isManualOracleString &&
                                                    handleAddCategorySubSplitBannerItem(
                                                      jobId,
                                                      catItem.id,
                                                      subItem.id
                                                    )
                                                  }
                                                >
                                                  + Banner
                                                </Typography.Link>
                                              </div>
                                            )}
                                        </div>

                                        {subItem.bannerSplitItems.length >
                                          1 && (
                                          <div
                                            style={{
                                              display: 'flex',
                                              flexDirection: 'column',
                                            }}
                                          >
                                            <div
                                              style={{
                                                height:
                                                  bIndex === 0
                                                    ? subIndex === 0
                                                      ? '20px'
                                                      : hasBannerSupport(
                                                          catItem.categoryName
                                                        )
                                                      ? '20px'
                                                      : '5px'
                                                    : '5px',
                                                marginBottom:
                                                  bIndex === 0
                                                    ? subIndex === 0
                                                      ? '4px'
                                                      : hasBannerSupport(
                                                          catItem.categoryName
                                                        )
                                                      ? '4px'
                                                      : '2px'
                                                    : '2px',
                                              }}
                                            >
                                              {bIndex === 0 && (
                                                <div
                                                  style={{
                                                    display: 'flex',
                                                    gap: '8px',
                                                  }}
                                                >
                                                  <Typography.Text
                                                    style={{
                                                      width: '45px',
                                                      fontSize: '11px',
                                                    }}
                                                  >
                                                    Qty
                                                  </Typography.Text>
                                                </div>
                                              )}
                                            </div>
                                            <div
                                              style={{
                                                display: 'flex',
                                                gap: '2px', // Reduced from 4px
                                                alignItems: 'center',
                                                height: '32px',
                                              }}
                                            >
                                              <Input
                                                style={{
                                                  width: '45px',
                                                  paddingLeft: '2px',
                                                  paddingRight: '2px',
                                                  textAlign: 'center',
                                                  ...(isFieldError(
                                                    bannerItem.id,
                                                    'quantity'
                                                  ) ||
                                                  (hasOverAllocation &&
                                                    lastChangedId ===
                                                      bannerItem.id)
                                                    ? errorInputStyle
                                                    : {}),
                                                }}
                                                value={bannerItem.quantity}
                                                disabled={isManualOracleString}
                                                onChange={(e) => {
                                                  handleCategorySubSplitBannerItemChange(
                                                    jobId,
                                                    catItem.id,
                                                    subItem.id,
                                                    bannerItem.id,
                                                    'quantity',
                                                    parseNumberInput(
                                                      e.target.value
                                                    )
                                                  );
                                                  clearFieldError(
                                                    bannerItem.id,
                                                    'quantity'
                                                  );
                                                }}
                                              />
                                              <Input
                                                style={{
                                                  width: '55px',
                                                  paddingLeft: '2px',
                                                  paddingRight: '2px',
                                                  textAlign: 'center',
                                                  ...(isFieldError(
                                                    bannerItem.id,
                                                    'percent'
                                                  ) ||
                                                  (hasOverAllocation &&
                                                    lastChangedId ===
                                                      bannerItem.id)
                                                    ? errorInputStyle
                                                    : {}),
                                                }}
                                                value={bannerItem.percent}
                                                suffix="%"
                                                disabled={isManualOracleString}
                                                onChange={(e) => {
                                                  handleCategorySubSplitBannerItemChange(
                                                    jobId,
                                                    catItem.id,
                                                    subItem.id,
                                                    bannerItem.id,
                                                    'percent',
                                                    parseNumberInput(
                                                      e.target.value
                                                    )
                                                  );
                                                  clearFieldError(
                                                    bannerItem.id,
                                                    'percent'
                                                  );
                                                }}
                                              />
                                              <Input
                                                style={{
                                                  width: '65px',
                                                  paddingLeft: '2px',
                                                  paddingRight: '2px',
                                                  textAlign: 'center',
                                                  ...(isFieldError(
                                                    bannerItem.id,
                                                    'amount'
                                                  ) ||
                                                  (hasOverAllocation &&
                                                    lastChangedId ===
                                                      bannerItem.id)
                                                    ? errorInputStyle
                                                    : {}),
                                                }}
                                                value={bannerItem.amount}
                                                prefix="$"
                                                disabled={isManualOracleString}
                                                onChange={(e) => {
                                                  handleCategorySubSplitBannerItemChange(
                                                    jobId,
                                                    catItem.id,
                                                    subItem.id,
                                                    bannerItem.id,
                                                    'amount',
                                                    parseNumberInput(
                                                      e.target.value
                                                    )
                                                  );
                                                  clearFieldError(
                                                    bannerItem.id,
                                                    'amount'
                                                  );
                                                }}
                                              />
                                            </div>
                                          </div>
                                        )}

                                        {subItem.bannerSplitItems.length >
                                          1 && (
                                          <div
                                            style={{
                                              height: '32px',
                                              display: 'flex',
                                              alignItems: 'center',
                                              marginTop:
                                                bIndex === 0
                                                  ? subIndex === 0
                                                    ? '24px'
                                                    : hasBannerSupport(
                                                        catItem.categoryName
                                                      )
                                                    ? '24px'
                                                    : '7px'
                                                  : '7px',
                                              marginLeft: '0px', // Reduced from -4px
                                            }}
                                          >
                                            <RevxButton
                                              type="text"
                                              size="small"
                                              disabled={isManualOracleString}
                                              onClick={() =>
                                                handleRemoveCategorySubSplitBannerItem(
                                                  jobId,
                                                  catItem.id,
                                                  subItem.id,
                                                  bannerItem.id
                                                )
                                              }
                                              style={{
                                                width: '24px',
                                                height: '24px',
                                                padding: 0,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                backgroundColor: '#fff',
                                                border: '1px solid #d9d9d9',
                                                borderRadius: '50%',
                                              }}
                                            >
                                              <DeleteOutlined
                                                style={{
                                                  fontSize: '11px',
                                                  color: '#8c8c8c',
                                                }}
                                              />
                                            </RevxButton>
                                          </div>
                                        )}
                                      </div>
                                    )
                                  )}
                                </Space>
                              </div>
                            )}
                        </div>
                      </div>
                    ))}
                  </Space>
                </div>
              </div>
            </div>
          ))}
          {items.length < categories.length && (
            <div>
              <Typography.Link
                style={{ fontSize: '12px' }}
                disabled={isManualOracleString}
                onClick={() =>
                  !isManualOracleString && handleAddCategorySplitItem(jobId)
                }
              >
                + Category
              </Typography.Link>
            </div>
          )}

          {/* Total/Remaining Rows */}
          {totals.quantity > 0 && isSplitActive && (
            <div style={{ paddingRight: '28px' }}>
              <div
                style={{
                  display: 'flex',
                  gap: '8px',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  borderTop: '1px solid #d9d9d9',
                  paddingTop: '8px',
                  marginTop: '8px',
                }}
              >
                <Typography.Text
                  strong
                  style={{
                    width: '100px',
                    textAlign: 'right',
                    fontSize: '12px',
                  }}
                >
                  Total:
                </Typography.Text>
                <Typography.Text
                  style={{
                    width: '45px',
                    textAlign: 'left',
                    paddingLeft: '4px',
                    fontSize: '12px',
                  }}
                >
                  {totals.quantity}
                </Typography.Text>
                <Typography.Text
                  style={{
                    width: '55px',
                    textAlign: 'left',
                    paddingLeft: '4px',
                    fontSize: '12px',
                  }}
                >
                  {displayPercent.toFixed(2)}%
                </Typography.Text>
                <Typography.Text
                  style={{
                    width: '65px',
                    textAlign: 'left',
                    paddingLeft: '4px',
                    fontSize: '12px',
                  }}
                >
                  ${displayAmount.toFixed(2)}
                </Typography.Text>
              </div>
              {getJobCalculations(jobId).remainingQuantity !== 0 && (
                <div
                  style={{
                    display: 'flex',
                    gap: '8px',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    marginTop: '4px',
                  }}
                >
                  <Typography.Text
                    style={{
                      width: '100px',
                      textAlign: 'right',
                      color: '#fa8c16',
                      fontWeight: 'bold',
                      fontSize: '12px',
                    }}
                  >
                    Remaining:
                  </Typography.Text>
                  <Typography.Text
                    style={{
                      width: '45px',
                      textAlign: 'left',
                      paddingLeft: '4px',
                      color: '#fa8c16',
                      fontWeight: 'bold',
                      fontSize: '12px',
                    }}
                  >
                    {getJobCalculations(jobId).remainingQuantity}
                  </Typography.Text>
                  <div style={{ width: '123px' }} />
                </div>
              )}
            </div>
          )}
        </Space>
      </div>
    );
  };

  return (
    <div className="cost-distribution-section">
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        <Form
          form={billingDetailForm}
          layout="vertical"
          className="billing-category-form"
        >
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
          >
            {/* Oracle manual string section - moved before Cost Distribution Category */}
            {user?.siteRole !== SiteRoleEnum.Buyer && (
              <div
                style={{
                  padding: '12px',
                  border: '1px solid #f0f0f0',
                  borderRadius: '8px',
                  backgroundColor: '#fafafa',
                  width: compact ? '100%' : '200%',
                }}
              >
                <div>
                  <Typography.Text strong>
                    Do you know your Oracle billing line?
                  </Typography.Text>
                  <Checkbox
                    style={{ marginLeft: '16px' }}
                    checked={isManualOracleString}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      if (isChecked) {
                        // Save current distributions before switching to manual
                        setSavedDistributions({ ...distributions });
                        // Reset to initial state
                        const initialDistributions: Record<
                          string,
                          Array<CategorySplitItem>
                        > = {};
                        if (isApplyToAllJobs) {
                          initialDistributions['-1'] = [
                            createInitialCategorySplitItem(),
                          ];
                        } else {
                          orderItems?.jobs?.forEach((job) => {
                            initialDistributions[job.jobId] = [
                              createInitialCategorySplitItem(),
                            ];
                          });
                        }
                        setDistributions(initialDistributions);
                      } else {
                        // Restore saved distributions
                        if (Object.keys(savedDistributions).length > 0) {
                          setDistributions({ ...savedDistributions });
                        }
                      }
                      setIsManualOracleString(isChecked);
                    }}
                  >
                    Yes
                  </Checkbox>
                  <Checkbox
                    checked={!isManualOracleString}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      if (isChecked) {
                        // Restore saved distributions
                        if (Object.keys(savedDistributions).length > 0) {
                          setDistributions({ ...savedDistributions });
                        }
                      } else {
                        // Save current distributions before switching to manual
                        setSavedDistributions({ ...distributions });
                        // Reset to initial state
                        const initialDistributions: Record<
                          string,
                          Array<CategorySplitItem>
                        > = {};
                        if (isApplyToAllJobs) {
                          initialDistributions['-1'] = [
                            createInitialCategorySplitItem(),
                          ];
                        } else {
                          orderItems?.jobs?.forEach((job) => {
                            initialDistributions[job.jobId] = [
                              createInitialCategorySplitItem(),
                            ];
                          });
                        }
                        setDistributions(initialDistributions);
                      }
                      setIsManualOracleString(!isChecked);
                    }}
                  >
                    No
                  </Checkbox>
                </div>
                {isManualOracleString && (
                  <>
                    <Input.TextArea
                      rows={2}
                      placeholder="Enter Oracle billing line"
                      value={manualOracleString}
                      onChange={(e) => setManualOracleString(e.target.value)}
                      style={{ marginTop: '12px' }}
                    />
                    {canShowProfileOptions && (
                      <div style={{ marginTop: '8px' }}>
                        {updateBillingContactCheckbox}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Same billing for all jobs - only show when not using manual oracle string and there are multiple jobs */}
            {!isManualOracleString &&
              orderItems?.jobs &&
              orderItems.jobs.length > 1 && (
                <div
                  style={{
                    padding: '12px',
                    border: '1px solid #f0f0f0',
                    borderRadius: '8px',
                    backgroundColor: '#fafafa',
                  }}
                >
                  <Typography.Text strong style={{ marginRight: '16px' }}>
                    Same billing for all jobs in cart?
                  </Typography.Text>
                  <Checkbox
                    checked={isApplyToAllJobs}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setIsApplyToAllJobs(true);
                      }
                    }}
                  >
                    Yes
                  </Checkbox>
                  <Checkbox
                    checked={!isApplyToAllJobs}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setIsApplyToAllJobs(false);
                      }
                    }}
                  >
                    No
                  </Checkbox>
                </div>
              )}

            {/* Cost Distribution Category section - only show when not using manual oracle string */}
            {!isManualOracleString && (
              <div>
                {isApplyToAllJobs
                  ? renderDistributionBlock('-1')
                  : orderItems?.jobs?.map((job) =>
                      renderDistributionBlock(job.jobId)
                    )}

                <div
                  style={{
                    padding: '12px',
                    border: '1px solid #f0f0f0',
                    borderRadius: '8px',
                    backgroundColor: '#fafafa',
                  }}
                >
                  <Typography.Text strong>Comments</Typography.Text>
                  <Input.TextArea
                    rows={4}
                    value={comments}
                    onChange={(e) => setComments(e.target.value)}
                    placeholder="Enter comments here"
                    style={{ marginTop: '8px', marginBottom: '8px' }}
                  />
                  {canShowProfileOptions && (
                    <Space direction="vertical" size={8}>
                      <Checkbox
                        checked={saveUserProfile}
                        onChange={(e) => setSaveUserProfile(e.target.checked)}
                      >
                        Update my profile with the selected Cost Distribution
                        values for future orders.
                      </Checkbox>
                      {updateBillingContactCheckbox}
                    </Space>
                  )}
                </div>
              </div>
            )}
          </div>
        </Form>
      </Space>
    </div>
  );
  }
);
